import { displayReturn } from "../Matches/matchNavigation";
import DiscoverySelect from "../Matches/DiscoverySelect";
import TournamentMatchManager from "./TournamentMatchManager";
import TournamentStandingsV2 from "./TournamentStandingsV2";
import { tournamentPublishedAt } from "./tournamentStandingLabels";
import "../Matches/PortalSurfaces.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { fetchUserAttributes, getCurrentUser } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  createTournamentEvent,
  getTournament,
  importTournamentArcJson,
  listOwnedMatches,
  listMyTournamentParticipants,
  listTournamentEvents,
  listTournamentLeaderboard,
  listTournamentParticipants,
  listTournamentResults,
  listTournamentsPage,
  recalculateTournamentLeaderboard,
  requestTournamentParticipation,
  createTournamentLeaderboardBannerUploadUrl,
  updateMatch,
  updateTournament,
  updateTournamentParticipant,
  updateTournamentEvent,
} from "../../lib/leaderboardApi";
import { buildAuthReturnPath, buildCurrentReturnTo } from "../../lib/authReturn";
import { getCompetitionApiEnvironment, isTournamentV2Enabled } from "../../lib/competitionApiEnvironment";
import { CompetitionAuthGate, CompetitionErrorState, TechnicalIdDisclosure } from "../Competition/CompetitionRoutePanels";
import { BoothDisplayStatus, BoothLeaderboardDisplay } from "../Leaderboard/LeaderboardMatchDetail";
import { preloadLeaderboardBanner } from "../Leaderboard/leaderboardBannerPreload";
import { formatCalendarDate, formatCalendarDateTime } from "../../utils/date";
import "../Leaderboard/Leaderboard.css";

const FAMILY_LABELS = {
  arc: "NRA ARC",
  bullseye: "Bullseye",
  uspsaCompetition: "USPSA Competition",
  uspsaSteelChallenge: "USPSA Steel Challenge",
  idpa: "IDPA",
};

const AGGREGATION_LABELS = {
  bestSingle: "Best single",
  sumAll: "Sum all",
  bestN: "Best N",
  averageTopN: "Average top N",
};

const IDENTITY_LABELS = {
  arcCompetitorId: "ARC competitor ID",
  competitorId: "Competitor ID",
  personId: "Range Pocket profile",
  participantId: "Walk-up participant",
};

const TOURNAMENT_STATUS_LABELS = {
  draft: "Draft",
  active: "Active",
  completed: "Completed",
  archived: "Archived",
};

const PARTICIPANT_STATUS_LABELS = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  revoked: "Revoked",
};

const TOURNAMENT_FAMILY_FILTERS = ["all", "arc", "bullseye", "uspsaCompetition", "uspsaSteelChallenge", "idpa"];
const PUBLIC_TOURNAMENT_STATUS_FILTERS = ["active", "completed", "all"];
const PUBLIC_TOURNAMENT_PAGE_LIMIT = 100;
const PUBLIC_TOURNAMENT_PREFETCH_PAGES = 10;
const BANNER_IMAGE_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);
const MAX_BANNER_IMAGE_BYTES = 5 * 1024 * 1024;
const TOURNAMENT_DEFAULT_LEADERBOARD_BANNER_SRC = "/tournament-default-leaderboard-banner.png";
const BULLSEYE_TOURNAMENT_BANNER_SRC = "/Leaderboard_Bullseye.svg";
const TOURNAMENT_LEADERBOARD_BANNER_LINK_AREAS = [
  {
    href: "https://arc.nra.org/",
    ariaLabel: "Open NRA America's Rifle Challenge",
    title: "NRA America's Rifle Challenge",
    className: "booth-banner-hotspot--nra-arc",
  },
  {
    to: "/RangePocket",
    ariaLabel: "Open Range Pocket",
    title: "RangePocket",
    className: "booth-banner-hotspot--rangepocket",
  },
];

function defaultTournamentBannerSrc(tournament) {
  return tournament?.scoringPolicyVersion === 2 && tournament.competitionFamily === "bullseye"
    ? BULLSEYE_TOURNAMENT_BANNER_SRC : TOURNAMENT_DEFAULT_LEADERBOARD_BANNER_SRC;
}

function resolveTournamentBannerSrc(tournament) {
  const customBannerUrl = String(tournament?.leaderboardBannerUrl || "").trim();
  if (!customBannerUrl) {
    return defaultTournamentBannerSrc(tournament);
  }
  return customBannerUrl;
}
const NRA_ARC_250_TOURNAMENT_ID = "1F726B0E-6A67-4912-A727-469DEB63397E";
const NRA_ARC_LEADERBOARD_BANNERS = [
  {
    src: "/leaderboard-banners/nra-arc-range-pocket.png",
    alt: "RangePocket sponsor banner",
    href: "https://www.armorysync.com/range-pocket",
  },
  {
    src: "/leaderboard-banners/nra-arc-ruger.png",
    alt: "Ruger sponsor banner",
    href: "https://ruger.com/products/harrier/models.html",
  },
  {
    src: "/leaderboard-banners/nra-arc-360-precision.png",
    alt: "360 Precision sponsor banner",
    href: "https://360precision.com/",
  },
  {
    src: "/leaderboard-banners/nra-arc-angry-hornet.png",
    alt: "Angry Hornet sponsor banner",
    href: "https://goangryhornet.com/",
  },
  {
    src: "/leaderboard-banners/nra-arc-tango-systems.png",
    alt: "Trango Systems sponsor banner",
    href: "https://www.trango-sys.com/",
  },
];

function formatDate(value) {
  if (!value) return "-";
  return formatCalendarDate(value, { year: "numeric", month: "short", day: "numeric" });
}

function formatDateTime(value) {
  return formatCalendarDateTime(value, {
    dateOptions: {
      month: "short",
      day: "numeric",
      year: "numeric",
    },
    timeOptions: {
      hour: "numeric",
      minute: "2-digit",
    },
  });
}

function familyLabel(value) {
  return FAMILY_LABELS[value] || value || "Tournament";
}

function aggregationLabel(value) {
  return AGGREGATION_LABELS[value] || value || "Aggregation";
}

function identityLabel(value) {
  return IDENTITY_LABELS[value] || value || "Competitor ID";
}

function tournamentIdentityLabel(tournament, aggregationConfig) {
  if (tournament?.competitionFamily === "arc") {
    return "Person / ARC ID";
  }
  return identityLabel(aggregationConfig.identityKey || tournament?.competitorIdentityKey || "competitorId");
}

function rowSourceLabel(row) {
  if (row?.sourceType === "nativeMatch" || row?.nativeMatchId) {
    return "Native Range Pocket";
  }
  if (row?.sourceType === "importedMatch" || row?.competitorExternalIdType === "arcCompetitorId") {
    return "Imported ARC";
  }
  return "Tournament";
}

function rowIdentityText(row) {
  const type = row?.competitorExternalIdType || "";
  const value = String(row?.competitorExternalId || "").trim();
  if (!value) return "";
  if (row?.identityRedacted || value.includes("@") || value.startsWith("private:")) return "Private identifier";
  if (type === "personId") return "Range Pocket profile";
  if (type === "participantId") return "Walk-up participant";
  return value;
}

function statusLabel(value) {
  return TOURNAMENT_STATUS_LABELS[value] || value || "Draft";
}

function matchStatusLabel(value) {
  return String(value || "scheduled")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function normalizeDisplayRowCount(value, fallback = 20) {
  if (String(value || "").trim().toLowerCase() === "all") {
    return "all";
  }
  const numericValue = Number.parseInt(String(value || ""), 10);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : fallback;
}

function normalizeDisplayFilterValue(value) {
  return String(value || "").trim();
}

function normalizeCategoryList(value) {
  if (Array.isArray(value)) {
    return value.map(normalizeDisplayFilterValue).filter(Boolean);
  }
  const normalized = normalizeDisplayFilterValue(value);
  return normalized ? [normalized] : [];
}

function normalizeOptionalPayloadText(value) {
  const normalized = String(value || "").trim();
  return normalized || undefined;
}

function getDisplayRowsParam(searchParams) {
  return searchParams.get("rows") || searchParams.get("top");
}

function normalizeTournamentFamilyFilter(value) {
  return TOURNAMENT_FAMILY_FILTERS.includes(value) ? value : "all";
}

function normalizePublicTournamentStatusFilter(value) {
  return PUBLIC_TOURNAMENT_STATUS_FILTERS.includes(value) ? value : "active";
}

function participantStatusLabel(value) {
  return PARTICIPANT_STATUS_LABELS[value] || value || "Pending";
}

function isTournamentActive(tournament) {
  return tournament?.status === "active";
}

function friendlyTournamentLoadError(err, { directorMode = false } = {}) {
  const message = err?.message || "Unable to load tournament.";
  if (/was not found|not found|404/i.test(message)) {
    return directorMode
      ? "This tournament is not available to the current account. Sign in with an owner or approved organizer account to manage it."
      : "This tournament is not available publicly. It may be private, archived, or no longer published.";
  }
  return message;
}

function tournamentStatusParam(statusFilter) {
  return statusFilter === "all" ? null : statusFilter;
}

async function loadPublicTournamentPages({ statusFilter, nextToken = null, maxPages = PUBLIC_TOURNAMENT_PREFETCH_PAGES } = {}) {
  const data = [];
  let cursor = nextToken || null;
  let pageCount = 0;

  do {
    const page = await listTournamentsPage({
      limit: PUBLIC_TOURNAMENT_PAGE_LIMIT,
      visibility: "public",
      status: tournamentStatusParam(statusFilter),
      sortDir: "asc",
      nextToken: cursor,
    });
    data.push(...(Array.isArray(page.data) ? page.data : []));
    cursor = page.nextToken || null;
    pageCount += 1;
  } while (cursor && pageCount < maxPages);

  return { data, nextToken: cursor };
}

async function loadOwnedArcMatchPages(userId, { maxPages = 10 } = {}) {
  const data = [];
  let cursor = null;
  let pageCount = 0;

  do {
    const page = await listOwnedMatches(userId, {
      limit: 100,
      sortDir: "desc",
      nextToken: cursor,
    });
    data.push(...(Array.isArray(page.data) ? page.data : []));
    cursor = page.nextToken || null;
    pageCount += 1;
  } while (cursor && pageCount < maxPages);

  return data;
}

function parsePolicyJson(raw) {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function templatePolicyLabel(tournament, familyPolicy) {
  const policyMode = tournament.templatePolicyMode || "none";
  const requiredName = tournament.requiredTemplateName || familyPolicy.requiredPresetName;
  const requiredId = tournament.requiredTemplateId || familyPolicy.requiredPresetId;
  const requiredLabel = requiredName || requiredId;

  if (policyMode === "required" || tournament.enforceTemplate === true) {
    return requiredLabel ? `Required: ${requiredLabel}` : "Required";
  }
  if (policyMode === "defaultOnly") {
    return requiredLabel ? `Default: ${requiredLabel}` : "Default only";
  }
  return "No template restriction";
}

function sourcePolicyLabel(tournament) {
  const labels = [];
  if (tournament.allowNativeMatches !== false) labels.push("Range Pocket matches");
  if (tournament.allowImportedMatches !== false) labels.push("imported files");
  return labels.length > 0 ? labels.join(" + ") : "none";
}

function eventApprovalLabel(event) {
  const status = event?.approvalStatus || "approved";
  if (status === "approved") return "Approved";
  if (status === "pending") return "Pending";
  if (status === "rejected") return "Rejected";
  if (status === "revoked") return "Revoked";
  return status;
}

function aggregationPolicyFacts(tournament) {
  if (tournament.scoringPolicyVersion === 2) {
    const mode = tournament.aggregationMode || "sumAll";
    const policy = tournament.scoringPolicy || {};
    const count = policy.countingMatchCount || 3;
    const minimum = policy.minimumMatches ?? (mode === "sumAll" ? "All eligible matches" : mode === "bestSingle" ? 1 : count);
    return [
      ["Aggregation", aggregationLabel(mode)],
      ["Counting matches", mode === "sumAll" ? "All eligible matches" : mode === "bestSingle" ? "Best single match" : `Best ${count} matches`],
      ["Minimum completed matches", String(minimum)],
      ["Counting results", "One per competitor, match and track"],
      ["Result eligibility", "Complete and confirmed / official"],
      ["Competitor identity", "Reused roster ID / verified account"],
      ["Score direction", tournament.competitionFamily === "arc" ? "Lower is better" : "Higher is better"],
      ["Match structures", policy.structureMode === "mixed" ? "Different courses allowed" : "Equivalent courses only"],
      ["Tracks", tournament.competitionFamily === "bullseye" ? "Separate divisions and .22 Only" : "Separate divisions"],
      ["Ties", "Shared places"],
      ["Match sources", sourcePolicyLabel(tournament)],
      ["Publication", tournamentPublishedAt(tournament) ? `Published ${formatDate(tournamentPublishedAt(tournament))}` : "Pending refresh"],
    ];
  }
  const aggregationConfig = parsePolicyJson(tournament.aggregationConfigRaw);
  const familyPolicy = parsePolicyJson(tournament.familyPolicyRaw);
  const mode = tournament.aggregationMode || "bestSingle";
  const countingAttemptCount = Number.parseInt(
    aggregationConfig.countingAttemptCount ?? (mode === "bestSingle" ? 1 : 3),
    10
  ) || 1;
  const allowedLevels = Array.isArray(familyPolicy.allowedLevelRawValues)
    ? familyPolicy.allowedLevelRawValues.filter(Boolean)
    : [];

  return [
    ["Aggregation", aggregationLabel(mode)],
    ["Counting scores", mode === "sumAll" ? "All eligible scores" : `${countingAttemptCount} per competitor`],
    ["Competitor identity", tournamentIdentityLabel(tournament, aggregationConfig)],
    ["Score direction", (aggregationConfig.scoreDirection || (tournament.competitionFamily === "arc" ? "lowerIsBetter" : "higherIsBetter")) === "lowerIsBetter" ? "Lower is better" : "Higher is better"],
    ["Match sources", sourcePolicyLabel(tournament)],
    ["Event approval", tournament.participantApprovalMode === "open" ? "Open" : "Owner approval required"],
    ["Match level", allowedLevels.length > 0 ? allowedLevels.join(", ") : "Any allowed family level"],
    ["Template", templatePolicyLabel(tournament, familyPolicy)],
    ["Cache", tournamentPublishedAt(tournament) ? `Published ${formatDate(tournamentPublishedAt(tournament))}` : "Pending refresh"],
  ];
}

function eventSourceLabel(event) {
  if (event?.sourceType === "nativeMatch") {
    return event.nativeMatchFamily === "arc" ? "Range Pocket ARC match" : "Range Pocket match";
  }
  if (event?.sourceType === "importedMatch") {
    return "Imported NRA ARC JSON";
  }
  return event?.sourceType || "-";
}

function usesNativeFullLeaderboard(event) {
  return Boolean(event?.sourceType === "nativeMatch" && ["arc", "bullseye"].includes(event?.nativeMatchFamily) && event?.nativeMatchId);
}

function scoreLabel(row) {
  if (row?.scoreDisplay) {
    return row.scoreDisplay;
  }
  return row?.rawScore ?? "-";
}

function resultStatusLabel(row) {
  return row?.isBestCountingResult ? "Counting" : "Attempt";
}

function competitorKey(row) {
  const type = row?.competitorExternalIdType || "competitorId";
  const id = row?.competitorExternalId || row?.competitorName || "unknown";
  return `${type}|${id}`;
}

function isLowerScoreBetter(tournament) {
  const aggregationConfig = parsePolicyJson(tournament?.aggregationConfigRaw);
  const scoreDirection = aggregationConfig.scoreDirection || (tournament?.competitionFamily === "arc" ? "lowerIsBetter" : "higherIsBetter");
  return scoreDirection === "lowerIsBetter";
}

function compareMatchResultRows(lhs, rhs, tournament) {
  const lhsRank = Number(lhs?.rank);
  const rhsRank = Number(rhs?.rank);
  if (Number.isFinite(lhsRank) && Number.isFinite(rhsRank) && lhsRank !== rhsRank) {
    return lhsRank - rhsRank;
  }

  const lhsScore = Number(lhs?.rawScore ?? 0);
  const rhsScore = Number(rhs?.rawScore ?? 0);
  if (lhsScore !== rhsScore) {
    return isLowerScoreBetter(tournament) ? lhsScore - rhsScore : rhsScore - lhsScore;
  }

  return String(lhs?.competitorName || lhs?.competitorExternalId || "")
    .localeCompare(String(rhs?.competitorName || rhs?.competitorExternalId || ""));
}

function tournamentLeaderboardPath(tournamentId) {
  return `/tournaments/${encodeURIComponent(tournamentId || "")}/leaderboard`;
}

function tournamentLeaderboardAllRowsPath(tournamentId) {
  return `${tournamentLeaderboardPath(tournamentId)}?rows=all`;
}

function uniqueSortedValues(rows, valueForRow) {
  const values = new Set();
  rows.forEach((row) => {
    const rawValue = valueForRow(row);
    const rowValues = Array.isArray(rawValue) ? rawValue : [rawValue];
    rowValues.forEach((item) => {
      const value = normalizeDisplayFilterValue(item);
      if (value) {
        values.add(value);
      }
    });
  });
  return [...values].sort((a, b) => a.localeCompare(b));
}

function arcMatchDisplayLeaderboardPath(matchId) {
  return `/matches/arc/${encodeURIComponent(matchId || "")}/display`;
}

function eventStatusCounts(events = []) {
  return events.reduce(
    (counts, event) => {
      const status = event?.approvalStatus || "approved";
      counts.total += 1;
      counts[status] = (counts[status] || 0) + 1;
      if (event?.sourceType === "importedMatch" || event?.importedMatchId) {
        counts.imported += 1;
      }
      return counts;
    },
    { total: 0, approved: 0, pending: 0, rejected: 0, revoked: 0, imported: 0 }
  );
}

function isApprovedEvent(event) {
  return !event?.detachedAt && (event?.approvalStatus || "approved") === "approved";
}

function shouldShowIncludedEvent(event, { includeInactive = false } = {}) {
  return includeInactive || isApprovedEvent(event);
}

function TournamentSectionHeader({ eyebrow, title, children }) {
  return (
    <div className="tournament-section-header">
      <div>
        <div className="owner-registration-section-label">{eyebrow}</div>
        {title && <h2>{title}</h2>}
      </div>
      {children ? <div className="tournament-section-actions">{children}</div> : null}
    </div>
  );
}

function TournamentLeaderboardSponsorBanner({ banner }) {
  if (!banner?.src) return null;

  const image = <img src={banner.src} alt={banner.alt || ""} loading="lazy" />;

  return (
    <div className="tournament-leaderboard-sponsor-banner">
      {banner.href ? (
        <a href={banner.href} target="_blank" rel="noopener noreferrer" aria-label={banner.alt || "Open sponsor link"}>
          {image}
        </a>
      ) : image}
    </div>
  );
}

function TournamentLeaderboardRanking({ rows, tournament, sponsorBanners = [], unavailable = false }) {
  if (unavailable) return <div className="tournament-empty-panel list-empty-panel"><strong>Standings unavailable</strong></div>;
  if (rows.length === 0) {
    return (
      <div className="tournament-empty-panel list-empty-panel">
        <div className="owner-registration-section-label">Standings</div>
        <strong>No aggregate results yet</strong>
        <div className="empty-filter-summary" aria-label="Tournament standings state">
          <span>{aggregationLabel(tournament?.aggregationMode)}</span>
          <span>Approved results: 0</span>
        </div>
      </div>
    );
  }

  return (
    <div className="rank-list">
      {rows.map((row, index) => {
        const rank = row.placement || index + 1;
        const rowCategories = normalizeCategoryList(row.categories);
        const identityText = rowIdentityText(row);
        const meta = [rowSourceLabel(row), row.state, row.division, row.classification, ...rowCategories].filter(Boolean);
        const rowKey = row.entryId || `${row.competitorExternalId}-${index}`;
        const sponsorBanner = sponsorBanners.length > 0 && (index + 1) % 10 === 0
          ? sponsorBanners[(Math.floor((index + 1) / 10) - 1) % sponsorBanners.length]
          : null;
        return (
          <React.Fragment key={rowKey}>
            <details className="rank-item">
              <summary className="rank-summary">
                <span className="rank-chip">#{rank}</span>
                <span className="rank-main">
                  <span className="rank-name">{row.competitorName || row.competitorExternalId || "Competitor"}</span>
                  {(identityText || meta.length > 0) && (
                    <span className="rank-meta-row">
                      {identityText && <span className="rank-division">{identityText}</span>}
                      {meta.map((value) => (
                        <span key={value} className="rank-meta-chip">{value}</span>
                      ))}
                    </span>
                  )}
                </span>
                <span className="rank-score">
                  <span className="rank-time">{scoreLabel(row)}</span>
                  <span className="rank-progress">
                    {row.totalAttemptCount > 1 ? `${row.countingAttemptCount || 1}/${row.totalAttemptCount} attempts` : aggregationLabel(tournament?.aggregationMode)}
                  </span>
                </span>
              </summary>
              <div className="rank-detail-grid">
                <div className="rank-detail-cell">
                  <div className="rank-detail-label">Counting Score</div>
                  <div className="rank-detail-value">{scoreLabel(row)}</div>
                </div>
                <div className="rank-detail-cell">
                  <div className="rank-detail-label">Attempts</div>
                  <div className="rank-detail-value">{row.totalAttemptCount || 1}</div>
                </div>
                <div className="rank-detail-cell">
                  <div className="rank-detail-label">Counting</div>
                  <div className="rank-detail-value">{row.countingAttemptCount || 1}</div>
                </div>
                <div className="rank-detail-cell">
                  <div className="rank-detail-label">Latest Match</div>
                  <div className="rank-detail-value">{formatDate(row.matchDate)}</div>
                </div>
                <div className="rank-detail-cell">
                  <div className="rank-detail-label">Identity</div>
                  <div className="rank-detail-value">{row.identityRedacted ? "Private identifier" : identityLabel(row.competitorExternalIdType)}</div>
                </div>
              </div>
            </details>
            {sponsorBanner && <TournamentLeaderboardSponsorBanner banner={sponsorBanner} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function AttemptsTable({ rows, eventNameById }) {
  if (rows.length === 0) return null;

  return (
    <div className="leaderboard-table-wrap tournament-attempts-table">
      <table className="leaderboard-table">
        <thead>
          <tr>
            <th>Competitor</th>
            <th>Attempt</th>
            <th>Score</th>
            <th>Status</th>
            <th>Event</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.entryId}>
              <td data-label="Competitor">
                <div className="tournament-attempt-competitor">
                  <span>{row.competitorName || row.competitorExternalId}</span>
                  <small>{rowIdentityText(row) || rowSourceLabel(row)}</small>
                </div>
              </td>
              <td data-label="Attempt">{row.attemptOrdinal || 1}</td>
              <td data-label="Score">{scoreLabel(row)}</td>
              <td data-label="Status">{resultStatusLabel(row)} · {rowSourceLabel(row)}</td>
              <td data-label="Event">{eventNameById.get(row.eventLinkId) || formatDate(row.matchDate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TournamentEventCards({
  events,
  tournament,
  canManageTournament,
  eventBusyKey,
  onEventStatus,
  resultCountByEvent = new Map(),
  unavailable = false,
  resultsUnavailable = false,
  displayActionLabel = "Open standings",
  emptyText = "No approved matches are included yet",
}) {
  if (unavailable) {
    return <div className="tournament-empty-panel list-empty-panel"><strong>Included matches unavailable</strong></div>;
  }
  if (events.length === 0) {
    return (
      <div className="tournament-empty-panel list-empty-panel">
        <div className="owner-registration-section-label">No matches</div>
        <strong>{emptyText}</strong>
        <div className="empty-filter-summary" aria-label="Included match state">
          <span>{canManageTournament ? "All approval states" : "Approved"}</span>
          <span>Included: 0</span>
        </div>
      </div>
    );
  }

  return (
    <div className="tournament-event-grid">
      {events.map((event) => {
        const approvalStatus = event.approvalStatus || "approved";
        const isNativeArc = usesNativeFullLeaderboard(event);
        const matchDetailPath = isNativeArc ? `/matches/${event.nativeMatchFamily}/${encodeURIComponent(event.nativeMatchId)}` : "";
        const resultCount = resultCountByEvent.get(event.eventLinkId) || 0;
        return (
          <article key={event.eventLinkId} className={`tournament-event-card ${isNativeArc ? "leaderboard-link-card" : ""}`}>
            {isNativeArc && (
              <Link
                to={matchDetailPath}
                className="card-click-target"
                aria-label={`Open ${event.displayName || "this match"}`}
              />
            )}
            <div className="tournament-event-card-main">
              <div className="tournament-event-card-title">
                {isNativeArc ? (
                  <Link to={matchDetailPath} className="leaderboard-inline-link">
                    {event.displayName || "Untitled Match"}
                  </Link>
                ) : (
                  event.displayName || "Untitled Match"
                )}
              </div>
              <div className="tournament-event-card-meta">
                <span>{formatDate(event.eventDate)}</span>
                <span>{eventSourceLabel(event)}</span>
                <span>{eventApprovalLabel(event)}</span>
                <span>{resultsUnavailable ? "Results unavailable" : `${resultCount} result row${resultCount === 1 ? "" : "s"}`}</span>
              </div>
              {event.importedMatchId && (
                <div className="leaderboard-meta">
                  {usesNativeFullLeaderboard(event) ? "Imported result file linked" : "Imported result linked"}
                </div>
              )}
              {canManageTournament && event.importedMatchId && (
                <TechnicalIdDisclosure label="Imported match ID" value={event.importedMatchId} />
              )}
            </div>
            <div className="tournament-event-card-actions">
              {isNativeArc && (
                <Link to={event.nativeMatchFamily === "bullseye" ? `/matches/bullseye/${encodeURIComponent(event.nativeMatchId)}/display` : arcMatchDisplayLeaderboardPath(event.nativeMatchId)} className="registration-auth-secondary leaderboard-inline-link">
                  {displayActionLabel}
                </Link>
              )}
              {canManageTournament && (
                <>
                  {approvalStatus !== "approved" && (
                    <button
                      type="button"
                      className="match-search-button"
                      onClick={() => onEventStatus(event.eventLinkId, "approved")}
                      disabled={Boolean(eventBusyKey) || !isTournamentActive(tournament)}
                    >
                      {eventBusyKey === `${event.eventLinkId}:approved` ? "Approving..." : "Approve"}
                    </button>
                  )}
                  {approvalStatus === "approved" ? (
                    <button
                      type="button"
                      className="match-search-button"
                      onClick={() => onEventStatus(event.eventLinkId, "revoked")}
                      disabled={Boolean(eventBusyKey) || !isTournamentActive(tournament)}
                    >
                      {eventBusyKey === `${event.eventLinkId}:revoked` ? "Revoking..." : "Revoke"}
                    </button>
                  ) : approvalStatus === "pending" ? (
                    <button
                      type="button"
                      className="match-search-button"
                      onClick={() => onEventStatus(event.eventLinkId, "rejected")}
                      disabled={Boolean(eventBusyKey) || !isTournamentActive(tournament)}
                    >
                      {eventBusyKey === `${event.eventLinkId}:rejected` ? "Rejecting..." : "Reject"}
                    </button>
                  ) : null}
                </>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}

function MatchLeaderboardsSection({ groups }) {
  if (groups.length === 0) {
    return null;
  }

  return (
    <section className="leaderboard-card tournament-display-section">
      <TournamentSectionHeader eyebrow="Match Leaderboards" title="Imported match results" />
      <div className="tournament-match-leaderboard-list">
        {groups.map(({ event, rows }) => (
          <article key={event.eventLinkId} className="tournament-match-leaderboard">
            <div className="tournament-event-card-main">
              <div className="tournament-event-card-title">{event.displayName || "Untitled Match"}</div>
              <div className="tournament-event-card-meta">
                <span>{formatDate(event.eventDate)}</span>
                <span>{eventSourceLabel(event)}</span>
                <span>{eventApprovalLabel(event)}</span>
              </div>
            </div>
            <div className="leaderboard-table-wrap">
              <table className="leaderboard-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Competitor</th>
                    <th>Score</th>
                    <th>Division</th>
                    <th>Class</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={row.entryId}>
                      <td data-label="Rank">{row.rank || index + 1}</td>
                      <td data-label="Competitor">
                        <div>{row.competitorName || row.competitorExternalId}</div>
                        {rowIdentityText(row) && <small>{rowIdentityText(row)}</small>}
                      </td>
                      <td data-label="Score">{scoreLabel(row)}</td>
                      <td data-label="Division">{row.division || "-"}</td>
                      <td data-label="Class">{row.classification || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function TournamentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearchQuery = String(searchParams.get("q") || "").trim();
  const initialFamilyFilter = normalizeTournamentFamilyFilter(searchParams.get("family") || "all");
  const initialStatusFilter = normalizePublicTournamentStatusFilter(searchParams.get("status") || "active");
  const [tournaments, setTournaments] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [searchInput, setSearchInput] = useState(initialSearchQuery);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [familyFilter, setFamilyFilter] = useState(initialFamilyFilter);
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [paginationError, setPaginationError] = useState("");

  useEffect(() => {
    const nextSearchQuery = String(searchParams.get("q") || "").trim();
    const nextFamilyFilter = normalizeTournamentFamilyFilter(searchParams.get("family") || "all");
    const nextStatusFilter = normalizePublicTournamentStatusFilter(searchParams.get("status") || "active");
    setSearchInput(nextSearchQuery);
    setSearchQuery(nextSearchQuery);
    setFamilyFilter(nextFamilyFilter);
    setStatusFilter(nextStatusFilter);
  }, [searchParams]);

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      setError("");
      setPaginationError("");
      try {
        const page = await loadPublicTournamentPages({ statusFilter });
        if (!active) return;
        setTournaments(Array.isArray(page.data) ? page.data : []);
        setNextToken(page.nextToken || null);
      } catch (err) {
        if (!active) return;
        setError(err?.message || "Unable to load public tournaments.");
        setTournaments([]);
        setNextToken(null);
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [statusFilter]);

  async function loadMore() {
    if (!nextToken || loadingMore) return;
    setLoadingMore(true);
    setPaginationError("");
    try {
      const page = await loadPublicTournamentPages({
        statusFilter,
        nextToken,
        maxPages: PUBLIC_TOURNAMENT_PREFETCH_PAGES,
      });
      setTournaments((current) => [...current, ...(Array.isArray(page.data) ? page.data : [])]);
      setNextToken(page.nextToken || null);
    } catch (err) {
      setPaginationError(err?.message || "Unable to load more tournaments right now. The tournaments already loaded are still available.");
    } finally {
      setLoadingMore(false);
    }
  }

  function handleSearchSubmit(event) {
    event.preventDefault();
    const nextQuery = searchInput.trim();
    setSearchQuery(nextQuery);
    const params = new URLSearchParams(searchParams);
    if (nextQuery) {
      params.set("q", nextQuery);
    } else {
      params.delete("q");
    }
    setSearchParams(params, { replace: true });
  }

  function handleClearFilters() {
    setSearchInput("");
    setSearchQuery("");
    setFamilyFilter("all");
    setStatusFilter("active");
    setSearchParams({}, { replace: true });
  }

  function handleFamilyFilterChange(nextFamilyFilter) {
    const normalizedFamily = normalizeTournamentFamilyFilter(nextFamilyFilter);
    setFamilyFilter(normalizedFamily);
    const params = new URLSearchParams(searchParams);
    if (normalizedFamily === "all") {
      params.delete("family");
    } else {
      params.set("family", normalizedFamily);
    }
    setSearchParams(params, { replace: true });
  }

  function handleStatusFilterChange(nextStatusFilter) {
    const normalizedStatus = normalizePublicTournamentStatusFilter(nextStatusFilter);
    setStatusFilter(normalizedStatus);
    const params = new URLSearchParams(searchParams);
    if (normalizedStatus === "active") {
      params.delete("status");
    } else {
      params.set("status", normalizedStatus);
    }
    setSearchParams(params, { replace: true });
  }

  const filteredTournaments = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return tournaments.filter((tournament) => {
      const matchesQuery = !query || [
        tournament.name,
        tournament.notes,
        tournament.requiredTemplateName,
        tournament.requiredTemplateId,
        familyLabel(tournament.competitionFamily),
        aggregationLabel(tournament.aggregationMode),
      ].some((value) => String(value || "").toLowerCase().includes(query));
      const matchesFamily = familyFilter === "all" || tournament.competitionFamily === familyFilter;
      const normalizedStatus = String(tournament.status || "draft").trim().toLowerCase();
      const matchesStatus = statusFilter === "all" || normalizedStatus === statusFilter;
      return matchesQuery && matchesFamily && matchesStatus;
    });
  }, [familyFilter, searchQuery, statusFilter, tournaments]);

  const hasActiveFilters = Boolean(searchQuery) || familyFilter !== "all" || statusFilter !== "active";
  const activeFilterLabels = [
    familyFilter !== "all" ? familyLabel(familyFilter) : null,
    statusFilter !== "active" ? statusLabel(statusFilter) : null,
    searchQuery ? `Search: ${searchQuery}` : null,
  ].filter(Boolean);
  const nextTournament = filteredTournaments[0] || null;
  return (
    <section className="leaderboard-page tournament-list-page">
      <Helmet>
        <title>Tournaments | Range Pocket Competition</title>
        <meta name="description" content="Browse public Range Pocket tournaments and aggregate leaderboards." />
      </Helmet>

      <div className="match-board-hero tournament-list-hero">
        <div>
          <div className="owner-registration-section-label">Public standings</div>
          <h1>Tournaments</h1>
        </div>
      </div>

      <div className="match-controls leaderboard-card">
        <div className="public-match-control-header">
          <div>
            <div className="match-stat-label">Search</div>
            <h2>Find a series</h2>
          </div>
          <div className="public-control-summary" aria-label="Tournament results summary">
            <strong>{loading ? "-" : filteredTournaments.length}</strong>
            <span>{statusFilter === "active" ? "active" : "shown"}</span>
          </div>
        </div>

        <form className="match-controls-row" onSubmit={handleSearchSubmit}>
          <input
            className="match-search"
            type="text"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Tournament or ruleset"
          />
          <button className="match-search-button" type="submit">Search</button>
          {hasActiveFilters && (
            <button className="match-search-button secondary" type="button" onClick={handleClearFilters}>
              Clear
            </button>
          )}
        </form>

        <div className="match-filter-grid" aria-label="Tournament filters">
          <label className="match-filter-field">
            <span>Family</span>
            <select className="match-sort" value={familyFilter} onChange={(event) => handleFamilyFilterChange(event.target.value)}>
              {TOURNAMENT_FAMILY_FILTERS.map((filterValue) => (
                <option key={filterValue} value={filterValue}>
                  {filterValue === "all" ? "All tournaments" : familyLabel(filterValue)}
                </option>
              ))}
            </select>
          </label>
          <label className="match-filter-field">
            <span>Status</span>
            <select className="match-sort" value={statusFilter} onChange={(event) => handleStatusFilterChange(event.target.value)}>
              {PUBLIC_TOURNAMENT_STATUS_FILTERS.map((filterValue) => (
                <option key={filterValue} value={filterValue}>
                  {filterValue === "all" ? "All statuses" : statusLabel(filterValue)}
                </option>
              ))}
            </select>
          </label>
        </div>

      </div>

      {loading && (
        <div className="leaderboard-card list-empty-panel status-loading-panel">
          <div className="owner-registration-section-label">Tournament standings</div>
          <strong>Loading public tournament list</strong>
          <span>Checking active series and standings status</span>
        </div>
      )}
      {!loading && error && (
        <section className="leaderboard-card app-gate-panel tournament-unavailable-panel">
          <div className="app-gate-eyebrow">Public tournaments</div>
          <h2>Tournaments could not load</h2>
          <p>{error}</p>
          <div className="app-gate-actions">
            <button type="button" className="match-search-button" onClick={() => window.location.reload()}>
              Try Again
            </button>
            <Link to="/matches" className="registration-auth-secondary leaderboard-inline-link">
              Matches
            </Link>
          </div>
        </section>
      )}
      {!loading && !error && paginationError && (
        <div className="leaderboard-card leaderboard-warning">{paginationError}</div>
      )}
      {!loading && !error && tournaments.length === 0 && (
        <div className="leaderboard-card leaderboard-empty-card list-empty-panel">
          <div>
            <div className="owner-registration-section-label">{hasActiveFilters ? "No results" : "No tournaments"}</div>
            <h2>{hasActiveFilters ? "No tournaments match these filters" : "No active tournaments"}</h2>
            {hasActiveFilters ? (
              <div className="empty-filter-summary" aria-label="Active filters">
                {activeFilterLabels.map((label) => (
                  <span key={label}>{label}</span>
                ))}
              </div>
            ) : (
              <div className="empty-filter-summary" aria-label="Default tournament filters">
                <span>Public</span>
                <span>Active</span>
              </div>
            )}
            {hasActiveFilters && (
              <div className="director-action-row">
                <button className="registration-auth-secondary leaderboard-inline-link detail-link-button" type="button" onClick={handleClearFilters}>
                  Reset
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {!loading && !error && tournaments.length > 0 && filteredTournaments.length === 0 && (
        <div className="leaderboard-card leaderboard-empty-card list-empty-panel">
          <div>
            <div className="owner-registration-section-label">No results</div>
            <h2>No tournaments match these filters</h2>
            <div className="empty-filter-summary" aria-label="Active filters">
              {activeFilterLabels.map((label) => (
                <span key={label}>{label}</span>
              ))}
            </div>
            <div className="director-action-row">
              <button className="registration-auth-secondary leaderboard-inline-link detail-link-button" type="button" onClick={handleClearFilters}>
                Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && filteredTournaments.length > 0 && (
        <div className="public-match-discovery-layout public-tournament-discovery-layout">
          <section className="leaderboard-card public-match-map-card public-tournament-overview-card">
            <div className="public-match-map-header">
              <div>
                <span className="match-stat-label">Overview</span>
                <strong>{filteredTournaments.length} tournaments</strong>
              </div>
              {nextTournament && <span>Next: {formatDate(nextTournament.startDate)}</span>}
            </div>
            <div className="registration-readiness-strip tournament-list-readiness-strip">
              <div className={`registration-readiness-item ${filteredTournaments.length > 0 ? "is-ready" : "is-neutral"}`}>
                <span>Shown</span>
                <strong>{filteredTournaments.length}</strong>
              </div>
              <div className={`registration-readiness-item ${filteredTournaments.filter((tournament) => tournament.status === "active").length > 0 ? "is-ready" : "is-neutral"}`}>
                <span>Active</span>
                <strong>{filteredTournaments.filter((tournament) => tournament.status === "active").length}</strong>
              </div>
              <div className={`registration-readiness-item ${filteredTournaments.filter((tournament) => tournament.status === "completed").length > 0 ? "is-ready" : "is-neutral"}`}>
                <span>Completed</span>
                <strong>{filteredTournaments.filter((tournament) => tournament.status === "completed").length}</strong>
              </div>
            </div>
          </section>
          <div className="public-match-list public-match-list--side">
            {filteredTournaments.map((tournament) => {
              const detailPath = `/tournaments/${encodeURIComponent(tournament.tournamentId)}`;
              const leaderboardPath = tournamentLeaderboardPath(tournament.tournamentId);
              const hasPublicStandings = Boolean(tournamentPublishedAt(tournament));
              const standingsStatus = tournamentPublishedAt(tournament)
                ? `Standings updated ${formatDate(tournamentPublishedAt(tournament))}`
                : "Standings available after scores post";
              return (
                <article
                  key={tournament.tournamentId}
                  className={`leaderboard-card match-card status-accent-${tournament.status === "completed" ? "completed" : "scheduled"} open-match-list-card public-match-list-card public-tournament-list-card leaderboard-link-card`}
                >
                  <Link
                    to={detailPath}
                    className="card-click-target"
                    aria-label={`Open details for ${tournament.name || "this tournament"}`}
                  />
                  <div className="leaderboard-card-row">
                    <strong className="open-match-card-title public-tournament-title-link">
                      {tournament.name || "Untitled Tournament"}
                    </strong>
                    <span className={`open-match-chip public-match-type-chip tournament-family-${tournament.competitionFamily || "generic"}`}>
                      {familyLabel(tournament.competitionFamily)}
                    </span>
                  </div>
                  <div className="open-match-card-meta">
                    {formatDate(tournament.startDate)} - {formatDate(tournament.endDate)}
                  </div>
                  <div className="open-match-card-pills">
                    <span className="open-match-card-pill">{statusLabel(tournament.status || "active")}</span>
                    <span className="open-match-card-pill">{aggregationLabel(tournament.aggregationMode)}</span>
                    <span className="open-match-card-pill">{sourcePolicyLabel(tournament)}</span>
                  </div>
                  <div className="match-card-footer">
                    <span>{standingsStatus}</span>
                    <span className="public-tournament-card-actions">
                      <Link to={detailPath} className="detail-link public-tournament-detail-link">Details</Link>
                      {hasPublicStandings ? (
                        <>
                          <Link to={leaderboardPath} className="detail-link public-tournament-display-link">Standings</Link>
                          <Link to={tournamentLeaderboardAllRowsPath(tournament.tournamentId)} className="detail-link">All scores</Link>
                        </>
                      ) : (
                        <span className="detail-link public-tournament-display-link tournament-pending-action" aria-disabled="true">
                          Standings pending
                        </span>
                      )}
                    </span>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      )}

      {nextToken && (
        <div className="leaderboard-load-more-row">
          <button className="leaderboard-load-more" type="button" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? "Loading…" : "Load more public tournaments"}
          </button>
        </div>
      )}
    </section>
  );
}

function TournamentDetailPage({ leaderboardOnly = false, directorMode = false } = {}) {
  const location = useLocation();
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tournament, setTournament] = useState(null);
  const [events, setEvents] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [resultEntries, setResultEntries] = useState([]);
  const [showAllAttempts, setShowAllAttempts] = useState(false);
  const [showFullPublicStandings, setShowFullPublicStandings] = useState(false);
  const [displayTopCount, setDisplayTopCount] = useState(() => normalizeDisplayRowCount(getDisplayRowsParam(searchParams), 20));
  const [displayDivisionFilter, setDisplayDivisionFilter] = useState(() => normalizeDisplayFilterValue(searchParams.get("division")));
  const [displayCategoryFilter, setDisplayCategoryFilter] = useState(() => normalizeDisplayFilterValue(searchParams.get("category")));
  const [canManageTournament, setCanManageTournament] = useState(false);
  const [currentUserId, setCurrentUserId] = useState("");
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [myParticipant, setMyParticipant] = useState(null);
  const [participantBusy, setParticipantBusy] = useState(false);
  const [eventBusyKey, setEventBusyKey] = useState("");
  const [participantMessage, setParticipantMessage] = useState("");
  const [participantError, setParticipantError] = useState("");
  const [recalculating, setRecalculating] = useState(false);
  const [statusBusy, setStatusBusy] = useState("");
  const [importMode, setImportMode] = useState("new");
  const [importEventLinkId, setImportEventLinkId] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [importLocation, setImportLocation] = useState({
    rangeName: "",
    rangeAddress: "",
    state: "",
    zipCode: "",
  });
  const [importBusy, setImportBusy] = useState(false);
  const [importMessage, setImportMessage] = useState("");
  const [importError, setImportError] = useState("");
  const [ownedArcMatches, setOwnedArcMatches] = useState([]);
  const [attachMatchId, setAttachMatchId] = useState("");
  const [attachBusy, setAttachBusy] = useState(false);
  const [attachMessage, setAttachMessage] = useState("");
  const [attachError, setAttachError] = useState("");
  const [lifecycleMessage, setLifecycleMessage] = useState("");
  const [lifecycleError, setLifecycleError] = useState("");
  const [adminMessage, setAdminMessage] = useState("");
  const [adminError, setAdminError] = useState("");
  const [bannerBusy, setBannerBusy] = useState(false);
  const [bannerMessage, setBannerMessage] = useState("");
  const [bannerError, setBannerError] = useState("");
  const [readyTournamentBannerSrc, setReadyTournamentBannerSrc] = useState(TOURNAMENT_DEFAULT_LEADERBOARD_BANNER_SRC);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [readFailures, setReadFailures] = useState({});
  const [retryingReads, setRetryingReads] = useState(false);
  const readGeneration = useRef(0);
  const detailWarnings = Object.values(readFailures);
  const standingsUnavailable = Boolean(readFailures.events || readFailures.leaderboard);
  const attemptsUnavailable = Boolean(readFailures.events || readFailures.results);
  const usesV2Membership = tournament?.scoringPolicyVersion === 2 && isTournamentV2Enabled();
  const [authRevision, setAuthRevision] = useState(0);
  useEffect(() => Hub.listen("auth", ({ payload }) => {
    if (["signedIn", "signedOut"].includes(payload.event)) setAuthRevision(value => value + 1);
  }), []);

  useEffect(() => {
    setDisplayTopCount(normalizeDisplayRowCount(getDisplayRowsParam(searchParams), 20));
    setDisplayDivisionFilter(normalizeDisplayFilterValue(searchParams.get("division")));
    setDisplayCategoryFilter(normalizeDisplayFilterValue(searchParams.get("category")));
  }, [searchParams]);

  useEffect(() => {
    let active = true;
    readGeneration.current += 1;

    async function load() {
      setLoading(true);
      setTournament(null);
      setEvents([]);
      setLeaderboard([]);
      setResultEntries([]);
      setError("");
      setReadFailures({});
      setRetryingReads(false);
      setCanManageTournament(false);
      setCurrentUserId("");
      setCurrentUserProfile(null);
      setParticipants([]);
      setMyParticipant(null);
      setEventBusyKey("");
      setParticipantMessage("");
      setParticipantError("");
      setStatusBusy("");
      setImportMessage("");
      setImportError("");
      setOwnedArcMatches([]);
      setAttachMatchId("");
      setAttachMessage("");
      setAttachError("");
      setShowFullPublicStandings(false);
      setLifecycleMessage("");
      setLifecycleError("");
      try {
        try {
          const user = await getCurrentUser();
          if (active) setCurrentUserId(String(user?.userId || "").trim());
        } catch { /* Public tournaments remain readable without a session. */ }
        if (!active) return;
        const [tournamentResult, eventResult, leaderboardResult, resultResult] = await Promise.allSettled([
          getTournament(tournamentId),
          listTournamentEvents(tournamentId),
          listTournamentLeaderboard(tournamentId),
          listTournamentResults(tournamentId, { eligibleOnly: !directorMode || leaderboardOnly }),
        ]);
        if (!active) return;

        if (tournamentResult.status !== "fulfilled") {
          throw tournamentResult.reason || new Error("Unable to load tournament.");
        }

        const tournamentData = tournamentResult.value;
        const failures = {};
        const eventRows = eventResult.status === "fulfilled" ? eventResult.value : [];
        const leaderboardRows = leaderboardResult.status === "fulfilled" ? leaderboardResult.value : [];
        const resultRows = resultResult.status === "fulfilled" ? resultResult.value : [];

        if (eventResult.status !== "fulfilled") {
          failures.events = "Included matches could not be loaded.";
        }
        if (leaderboardResult.status !== "fulfilled") {
          failures.leaderboard = "Tournament standings could not be loaded.";
        }
        if (resultResult.status !== "fulfilled") {
          failures.results = "Detailed result history could not be loaded.";
        }

        setTournament(tournamentData || null);
        setEvents(Array.isArray(eventRows) ? eventRows : []);
        setLeaderboard(Array.isArray(leaderboardRows) ? leaderboardRows : []);
        setResultEntries(Array.isArray(resultRows) ? resultRows : []);
        setReadFailures(failures);

        if (!directorMode || leaderboardOnly) return;

        try {
          const user = await getCurrentUser();
          const attrs = await fetchUserAttributes();
          const resolvedUserId = String(attrs?.sub || user?.userId || "").trim();
          const ownerUserId = String(tournamentData?.ownerUserId || "").trim();
          const isOwner = Boolean(ownerUserId && resolvedUserId === ownerUserId);
          if (active) {
            setCurrentUserId(resolvedUserId);
            setCurrentUserProfile({
              displayName: attrs?.name || attrs?.preferred_username || attrs?.email || "",
              email: attrs?.email || "",
            });
            setCanManageTournament(isOwner);
          }

          if (resolvedUserId && active) {
            const reads = isOwner ? [
              { key: "participants", message: "Organizer requests could not be loaded.",
                promise: listTournamentParticipants(tournamentId), update: setParticipants },
              { key: "ownedMatches", message: "Your available ARC matches could not be loaded.",
                promise: tournamentData?.scoringPolicyVersion !== 2 && tournamentData?.competitionFamily === "arc" && tournamentData?.allowNativeMatches !== false
                  ? loadOwnedArcMatchPages(resolvedUserId) : Promise.resolve([]), update: setOwnedArcMatches },
            ] : [
              { key: "myParticipation", message: "Your organizer access status could not be loaded.",
                promise: listMyTournamentParticipants(), update: rows => setMyParticipant(rows
                  .find(row => row.tournamentId === tournamentId) || null) },
            ];
            const results = await Promise.allSettled(reads.map(read => read.promise));
            if (!active) return;
            const denied = results.find(result => result.status === "rejected" && [401, 403].includes(result.reason?.status));
            if (denied) throw denied.reason;
            const auxiliaryFailures = {};
            results.forEach((result, index) => {
              const read = reads[index];
              if (result.status === "fulfilled") read.update(Array.isArray(result.value) ? result.value : []);
              else auxiliaryFailures[read.key] = read.message;
            });
            setReadFailures(current => ({ ...current, ...auxiliaryFailures }));
          }
        } catch {
          if (active) {
            setCanManageTournament(false);
            setCurrentUserId("");
            setCurrentUserProfile(null);
          }
        }
      } catch (err) {
        if (!active) return;
        setError(friendlyTournamentLoadError(err, { directorMode }));
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
      readGeneration.current += 1;
    };
  }, [authRevision, directorMode, leaderboardOnly, tournamentId]);

  function clearReadFailures(keys) {
    setReadFailures(current => Object.fromEntries(Object.entries(current).filter(([key]) => !keys.includes(key))));
  }

  async function refreshCommittedMembership(link) {
    const generation = readGeneration.current;
    setEvents(current => [...current.filter(event => event.eventLinkId !== link.eventLinkId), link]);
    const reads = [
      ["tournament", () => getTournament(tournamentId, { auth: true }), setTournament, "Tournament details could not be refreshed."],
      ["events", () => listTournamentEvents(tournamentId, { auth: true }), setEvents, "Included matches could not be refreshed."],
      ["leaderboard", () => listTournamentLeaderboard(tournamentId, { auth: true }), setLeaderboard, "Standings could not be refreshed."],
      ["results", () => listTournamentResults(tournamentId, { auth: true }), setResultEntries, "Result history could not be refreshed."],
    ];
    const failures = {};
    for (const [key, read, update, message] of reads) {
      try { const value = await read(); if (generation !== readGeneration.current) return; update(value); clearReadFailures([key]); }
      catch { failures[key] = message; }
    }
    if (generation !== readGeneration.current) return;
    setReadFailures(current => ({ ...current, ...failures }));
    if (Object.keys(failures).length) throw new Error("Membership saved; refresh incomplete.");
  }

  async function retryTournamentReads() {
    if (retryingReads) return;
    const generation = readGeneration.current;
    setRetryingReads(true);
    const readers = {
      tournament: [() => getTournament(tournamentId, { auth: true }), setTournament],
      events: [() => listTournamentEvents(tournamentId), setEvents],
      leaderboard: [() => listTournamentLeaderboard(tournamentId), setLeaderboard],
      results: [() => listTournamentResults(tournamentId, { eligibleOnly: !directorMode || leaderboardOnly }), setResultEntries],
      participants: [() => listTournamentParticipants(tournamentId), setParticipants],
      ownedMatches: [() => loadOwnedArcMatchPages(currentUserId), setOwnedArcMatches],
      myParticipation: [() => listMyTournamentParticipants(), rows => setMyParticipant(rows
        .find(row => row.tournamentId === tournamentId) || null)],
    };
    for (const key of Object.keys(readFailures)) {
      try {
        const [read, update] = readers[key];
        const rows = await read();
        if (generation !== readGeneration.current) return;
        update(key === "tournament" ? rows : Array.isArray(rows) ? rows : []);
        clearReadFailures([key]);
      } catch (error) {
        if (generation !== readGeneration.current) return;
        if ([401, 403].includes(error?.status) && ["participants", "ownedMatches", "myParticipation"].includes(key)) {
          setCanManageTournament(false);
          setCurrentUserId("");
          setCurrentUserProfile(null);
          break;
        }
      }
    }
    if (generation === readGeneration.current) setRetryingReads(false);
  }

  async function handleRecalculateLeaderboard() {
    if (recalculating || !isTournamentActive(tournament)) return;

    setRecalculating(true);
    setAdminMessage("");
    setAdminError("");
    setBannerMessage("");
    setBannerError("");
    try {
      const payload = await recalculateTournamentLeaderboard(tournamentId);
      const [tournamentData, leaderboardRows, resultRows] = await Promise.all([
        getTournament(tournamentId, { auth: true }),
        listTournamentLeaderboard(tournamentId, { auth: true }),
        listTournamentResults(tournamentId, { auth: true }),
      ]);
      setTournament(tournamentData || tournament);
      setLeaderboard(Array.isArray(leaderboardRows) ? leaderboardRows : []);
      setResultEntries(Array.isArray(resultRows) ? resultRows : []);
      clearReadFailures(["leaderboard", "results"]);
      const leaderboardCount = payload?.data?.leaderboardCount;
      setAdminMessage(payload?.meta?.state === "stale" || payload?.meta?.state === "pending"
        ? "Refresh pending. Showing the last published standings."
        : Number.isInteger(leaderboardCount)
          ? `Recalculated ${leaderboardCount} leaderboard ${leaderboardCount === 1 ? "row" : "rows"}.`
          : "Standings refreshed.");
    } catch (err) {
      setAdminError(err?.message || "Unable to recalculate leaderboard.");
    } finally {
      setRecalculating(false);
    }
  }

  async function refreshTournamentParticipants() {
    if (canManageTournament) {
      const rows = await listTournamentParticipants(tournamentId);
      setParticipants(Array.isArray(rows) ? rows : []);
      return;
    }

    if (currentUserId) {
      const rows = await listMyTournamentParticipants();
      setMyParticipant((Array.isArray(rows) ? rows : [])
        .find((row) => row.tournamentId === tournamentId) || null);
    }
  }

  async function handleRequestTournamentAccess() {
    if (participantBusy || !isTournamentActive(tournament)) return;

    setParticipantBusy(true);
    setParticipantMessage("");
    setParticipantError("");
    try {
      const participant = await requestTournamentParticipation(tournamentId, {
        participantDisplayName: currentUserProfile?.displayName || "",
        contactEmail: currentUserProfile?.email || "",
      });
      setMyParticipant(participant || null);
      setParticipantMessage(participant?.participantStatus === "approved"
        ? "Tournament access is approved."
        : "Request sent to the tournament owner.");
    } catch (err) {
      setParticipantError(err?.message || "Unable to request tournament access.");
    } finally {
      setParticipantBusy(false);
    }
  }

  async function handleParticipantStatus(participantUserId, participantStatus) {
    if (participantBusy || !isTournamentActive(tournament)) return;

    setParticipantBusy(true);
    setParticipantMessage("");
    setParticipantError("");
    try {
      await updateTournamentParticipant(tournamentId, participantUserId, { participantStatus });
      await refreshTournamentParticipants();
      setParticipantMessage(`Participant ${participantStatus}.`);
    } catch (err) {
      setParticipantError(err?.message || "Unable to update participant.");
    } finally {
      setParticipantBusy(false);
    }
  }

  async function handleEventStatus(eventLinkId, approvalStatus) {
    const busyKey = `${eventLinkId}:${approvalStatus}`;
    if (eventBusyKey || !isTournamentActive(tournament)) return;

    setEventBusyKey(busyKey);
    setAdminMessage("");
    setAdminError("");
    try {
      await updateTournamentEvent(tournamentId, eventLinkId, { approvalStatus });
      const [tournamentData, eventRows, leaderboardRows, resultRows] = await Promise.all([
        getTournament(tournamentId, { auth: true }),
        listTournamentEvents(tournamentId, { auth: true }),
        listTournamentLeaderboard(tournamentId, { auth: true }),
        listTournamentResults(tournamentId, { auth: true }),
      ]);
      setTournament(tournamentData || tournament);
      setEvents(Array.isArray(eventRows) ? eventRows : []);
      setLeaderboard(Array.isArray(leaderboardRows) ? leaderboardRows : []);
      setResultEntries(Array.isArray(resultRows) ? resultRows : []);
      clearReadFailures(["events", "leaderboard", "results"]);
      setAdminMessage(`Event ${approvalStatus}.`);
    } catch (err) {
      setAdminError(err?.message || "Unable to update event approval.");
    } finally {
      setEventBusyKey("");
    }
  }

  async function handleAttachExistingArcMatch(event) {
    event.preventDefault();
    if (!canManageTournament || attachBusy || !isTournamentActive(tournament)) return;
    if (!selectedAttachMatch) {
      setAttachError("Choose an ARC match to attach.");
      return;
    }

    const matchId = selectedAttachMatch.matchId;
    const displayName = selectedAttachMatch.matchName || "Untitled Match";
    const generation = readGeneration.current;
    setAttachBusy(true);
    setAttachMessage("");
    setAttachError("");
    setAdminMessage("");
    setAdminError("");

    try {
      const link = await createTournamentEvent(tournamentId, {
        sourceType: "nativeMatch",
        nativeMatchFamily: "arc",
        nativeMatchId: matchId,
        displayName,
        eventDate: selectedAttachMatch.startDate || selectedAttachMatch.matchDate || new Date().toISOString(),
        approvalStatus: "approved",
        isCountingEligible: true,
      });

      let metadataWarning = "";
      try {
        await updateMatch(matchId, {
          tournamentId: tournament.tournamentId,
          tournamentName: tournament.name,
          tournamentEventLinkId: link?.eventLinkId,
        });
      } catch (matchErr) {
        metadataWarning = matchErr?.message || "Match metadata could not be updated.";
      }
      if (generation !== readGeneration.current) return;

      // The attachment is committed. A refresh failure must not invite a duplicate write.
      setEvents(current => [...current.filter(row => row.eventLinkId !== link?.eventLinkId), {
        sourceType: "nativeMatch", nativeMatchFamily: "arc", nativeMatchId: matchId,
        displayName, eventDate: selectedAttachMatch.startDate || selectedAttachMatch.matchDate,
        approvalStatus: "approved", isCountingEligible: true, ...link,
      }]);
      setAttachMatchId("");
      setAttachMessage(metadataWarning
        ? `Attached ${displayName}. ${metadataWarning}`
        : `Attached ${displayName} to this tournament.`);

      const reads = [
        { key: "tournament", read: () => getTournament(tournamentId, { auth: true }), update: setTournament,
          message: "Tournament details could not be refreshed." },
        { key: "events", read: () => listTournamentEvents(tournamentId, { auth: true }), update: setEvents,
          message: "Included matches could not be refreshed." },
        { key: "leaderboard", read: () => listTournamentLeaderboard(tournamentId, { auth: true }), update: setLeaderboard,
          message: "Tournament standings could not be refreshed." },
        { key: "results", read: () => listTournamentResults(tournamentId, { auth: true }), update: setResultEntries,
          message: "Detailed result history could not be refreshed." },
        { key: "ownedMatches", read: () => currentUserId ? loadOwnedArcMatchPages(currentUserId) : Promise.resolve(ownedArcMatches),
          update: setOwnedArcMatches, message: "Your available ARC matches could not be refreshed." },
      ];
      const failures = {};
      for (const { key, read, update, message } of reads) {
        try {
          const value = await read();
          if (generation !== readGeneration.current) return;
          update(key === "tournament" ? value || tournament : Array.isArray(value) ? value : []);
        } catch {
          if (generation !== readGeneration.current) return;
          failures[key] = message;
        }
      }
      setReadFailures(current => ({ ...Object.fromEntries(Object.entries(current)
        .filter(([key]) => !reads.some(read => read.key === key))), ...failures }));
    } catch (err) {
      if (generation === readGeneration.current) setAttachError(err?.message || "Unable to attach this match.");
    } finally {
      if (generation === readGeneration.current) setAttachBusy(false);
    }
  }

  async function handleTournamentStatus(nextStatus) {
    if (!canManageTournament || statusBusy || nextStatus === tournament?.status) return;

    setStatusBusy(nextStatus);
    setLifecycleMessage("");
    setLifecycleError("");
    try {
      const updated = await updateTournament(tournamentId, { status: nextStatus });
      setTournament(updated || tournament);
      setLifecycleMessage(`Tournament status changed to ${statusLabel(nextStatus)}.`);
    } catch (err) {
      setLifecycleError(err?.message || "Unable to update tournament status.");
    } finally {
      setStatusBusy("");
    }
  }

  async function handleTournamentBannerUpload(event) {
    const file = event.target.files?.[0] || null;
    event.target.value = "";

    if (!file) {
      return;
    }

    setBannerMessage("");
    setBannerError("");

    if (!BANNER_IMAGE_TYPES.has(file.type)) {
      setBannerError("Upload a PNG, JPG, or WebP banner image.");
      return;
    }

    if (file.size > MAX_BANNER_IMAGE_BYTES) {
      setBannerError("Use a banner image under 5 MB.");
      return;
    }

    setBannerBusy(true);

    try {
      const upload = await createTournamentLeaderboardBannerUploadUrl(tournamentId, {
        contentType: file.type
      });
      const uploadResponse = await fetch(upload.uploadUrl, {
        method: upload.method || "PUT",
        headers: upload.headers || { "content-type": file.type },
        body: file
      });

      if (!uploadResponse.ok) {
        throw new Error(`S3 upload failed (${uploadResponse.status})`);
      }

      const updatedTournament = await updateTournament(tournamentId, {
        leaderboardBannerS3Key: upload.s3Key,
        leaderboardBannerContentType: file.type,
        leaderboardBannerUpdatedAt: new Date().toISOString()
      });

      setTournament(updatedTournament || tournament);
      setBannerMessage("Tournament leaderboard banner updated.");
    } catch (err) {
      setBannerError(err?.message || "Failed to upload tournament banner.");
    } finally {
      setBannerBusy(false);
    }
  }

  async function handleImportArcJson(event) {
    event.preventDefault();
    if (!canManageTournament || importBusy || !isTournamentActive(tournament)) return;

    if (!importFile) {
      setImportError("Choose a JSON file to import.");
      return;
    }
    if (importMode === "existing" && !importEventLinkId) {
      setImportError("Choose the tournament match to attach this JSON to.");
      return;
    }

    setImportBusy(true);
    setImportMessage("");
    setImportError("");
    try {
      const rawJson = await importFile.text();
      const payload = {
        rawJson,
        sourceFileName: importFile.name,
        rangeName: normalizeOptionalPayloadText(importLocation.rangeName),
        rangeAddress: normalizeOptionalPayloadText(importLocation.rangeAddress),
        state: normalizeOptionalPayloadText(importLocation.state),
        zipCode: normalizeOptionalPayloadText(importLocation.zipCode),
        ...(importMode === "existing" ? { eventLinkId: importEventLinkId } : {})
      };
      const imported = await importTournamentArcJson(tournamentId, payload);
      const [tournamentData, eventRows, leaderboardRows, resultRows] = await Promise.all([
        getTournament(tournamentId, { auth: true }),
        listTournamentEvents(tournamentId, { auth: true }),
        listTournamentLeaderboard(tournamentId, { auth: true }),
        listTournamentResults(tournamentId, { auth: true }),
      ]);
      setTournament(tournamentData || tournament);
      setEvents(Array.isArray(eventRows) ? eventRows : []);
      setLeaderboard(Array.isArray(leaderboardRows) ? leaderboardRows : []);
      setResultEntries(Array.isArray(resultRows) ? resultRows : []);
      clearReadFailures(["events", "leaderboard", "results"]);
      setImportFile(null);
      setImportLocation({ rangeName: "", rangeAddress: "", state: "", zipCode: "" });
      setImportMessage(
        importMode === "existing"
          ? `Imported ${imported?.importedResultCount ?? 0} result rows into the selected match.`
          : `Created imported match with ${imported?.importedResultCount ?? 0} result rows.`
      );
    } catch (err) {
      setImportError(err?.message || "Unable to import this ARC JSON file.");
    } finally {
      setImportBusy(false);
    }
  }

  function setImportLocationField(fieldName, value) {
    setImportLocation((current) => ({
      ...current,
      [fieldName]: value
    }));
    setImportMessage("");
    setImportError("");
  }

  const approvedEvents = useMemo(
    () => events.filter(isApprovedEvent),
    [events]
  );

  const visibleIncludedEvents = useMemo(
    () => events.filter((event) => shouldShowIncludedEvent(event, {
      includeInactive: !leaderboardOnly && directorMode && canManageTournament,
    })),
    [canManageTournament, directorMode, events, leaderboardOnly]
  );

  const approvedEventIds = useMemo(
    () => new Set(approvedEvents.map((event) => event.eventLinkId).filter(Boolean)),
    [approvedEvents]
  );

  const approvedResultEntries = useMemo(
    () => resultEntries.filter((row) => !row.eventLinkId || approvedEventIds.has(row.eventLinkId)),
    [approvedEventIds, resultEntries]
  );

  const countingRows = useMemo(
    () => leaderboard
      .filter((row) => row.isBestCountingResult !== false)
      .filter((row) => !row.eventLinkId || approvedEventIds.has(row.eventLinkId))
      .sort((a, b) => (a.placement || 9999) - (b.placement || 9999)),
    [approvedEventIds, leaderboard]
  );

  const eventNameById = useMemo(
    () => new Map(events.map((event) => [event.eventLinkId, event.displayName || "Untitled Event"])),
    [events]
  );

  const resultCountByEvent = useMemo(() => {
    const counts = new Map();
    for (const row of resultEntries) {
      if (!row.eventLinkId) continue;
      counts.set(row.eventLinkId, (counts.get(row.eventLinkId) || 0) + 1);
    }
    return counts;
  }, [resultEntries]);

  const policyFacts = useMemo(
    () => tournament ? aggregationPolicyFacts(tournament) : [],
    [tournament]
  );

  const hasApprovedOrganizerAccess = myParticipant?.participantStatus === "approved";
  const canCreateArcMatchForTournament = Boolean(
    tournament?.competitionFamily === "arc" &&
    tournament?.allowNativeMatches !== false &&
    isTournamentActive(tournament) &&
    directorMode &&
    (canManageTournament || hasApprovedOrganizerAccess)
  );
  const canAttachExistingArcMatch = Boolean(
    !usesV2Membership &&
    tournament?.competitionFamily === "arc" &&
    tournament?.allowNativeMatches !== false &&
    isTournamentActive(tournament) &&
    directorMode &&
    canManageTournament
  );

  const attachableArcEvents = useMemo(
    () => events.filter((event) => (
      event.sourceType === "nativeMatch" &&
      event.nativeMatchFamily === "arc" &&
      event.nativeMatchId
    )),
    [events]
  );

  const linkedNativeArcMatchIds = useMemo(
    () => new Set(attachableArcEvents.map((event) => String(event.nativeMatchId || "")).filter(Boolean)),
    [attachableArcEvents]
  );

  const attachableOwnedArcMatches = useMemo(
    () => ownedArcMatches
      .map((match) => ({
        ...match,
        matchId: String(match?.matchId || match?.id || "").trim(),
        matchName: match?.matchName || match?.name || "Untitled Match",
      }))
      .filter((match) => {
        if (!match.matchId || linkedNativeArcMatchIds.has(match.matchId)) return false;
        const linkedTournamentId = String(match?.tournamentId || "").trim();
        return !linkedTournamentId || linkedTournamentId === String(tournament?.tournamentId || "").trim();
      }),
    [linkedNativeArcMatchIds, ownedArcMatches, tournament?.tournamentId]
  );

  const selectedAttachMatch = useMemo(
    () => attachableOwnedArcMatches.find((match) => match.matchId === attachMatchId) || null,
    [attachMatchId, attachableOwnedArcMatches]
  );

  const leaderboardByCompetitor = useMemo(
    () => new Map(leaderboard.map((row) => [competitorKey(row), row])),
    [leaderboard]
  );

  const allAttemptRows = useMemo(
    () => [...approvedResultEntries].sort((a, b) => {
      const nameCompare = String(a.competitorName || a.competitorExternalId || "")
        .localeCompare(String(b.competitorName || b.competitorExternalId || ""));
      if (nameCompare !== 0) return nameCompare;
      if ((a.attemptOrdinal || 0) !== (b.attemptOrdinal || 0)) {
        return (a.attemptOrdinal || 0) - (b.attemptOrdinal || 0);
      }
      return String(a.matchDate || "").localeCompare(String(b.matchDate || ""));
    }),
    [approvedResultEntries]
  );

  const competitorHistoryRows = useMemo(() => {
    const groups = new Map();
    for (const row of allAttemptRows) {
      const key = competitorKey(row);
      groups.set(key, [...(groups.get(key) || []), row]);
    }

    return [...groups.entries()]
      .map(([key, attempts]) => {
        const sortedAttempts = [...attempts].sort((a, b) => {
          if ((a.attemptOrdinal || 0) !== (b.attemptOrdinal || 0)) {
            return (a.attemptOrdinal || 0) - (b.attemptOrdinal || 0);
          }
          return String(a.matchDate || "").localeCompare(String(b.matchDate || ""));
        });
        const countingAttempts = sortedAttempts.filter((row) => row.isBestCountingResult);
        const leaderboardRow = leaderboardByCompetitor.get(key);
        const eventNames = [...new Set(sortedAttempts.map((row) => (
          eventNameById.get(row.eventLinkId) || formatDate(row.matchDate)
        )))].filter(Boolean);

        const attemptDates = sortedAttempts
          .map((row) => row.matchDate)
          .filter(Boolean)
          .sort();

        return {
          key,
          competitorName: sortedAttempts[0]?.competitorName || sortedAttempts[0]?.competitorExternalId || "Unknown",
          competitorIdentityText: rowIdentityText(sortedAttempts[0]) || rowSourceLabel(sortedAttempts[0]),
          countingScore: scoreLabel(leaderboardRow || countingAttempts[0] || sortedAttempts[0]),
          countingCount: countingAttempts.length,
          attemptCount: sortedAttempts.length,
          eventNames,
          latestDate: attemptDates[attemptDates.length - 1],
        };
      })
      .sort((a, b) => a.competitorName.localeCompare(b.competitorName));
  }, [allAttemptRows, eventNameById, leaderboardByCompetitor]);

  const matchLeaderboardGroups = useMemo(() => {
    const resultsByEventId = new Map();
    for (const row of approvedResultEntries) {
      if (!row.eventLinkId) continue;
      resultsByEventId.set(row.eventLinkId, [...(resultsByEventId.get(row.eventLinkId) || []), row]);
    }

    return approvedEvents
      .filter((event) => !usesNativeFullLeaderboard(event))
      .map((event) => {
        const rows = [...(resultsByEventId.get(event.eventLinkId) || [])]
          .sort((lhs, rhs) => compareMatchResultRows(lhs, rhs, tournament));
        return { event, rows };
      })
      .filter((group) => group.rows.length > 0);
  }, [approvedEvents, approvedResultEntries, tournament]);

  const eventCounts = useMemo(
    () => eventStatusCounts(events),
    [events]
  );

  const approvedImportedCount = useMemo(
    () => approvedEvents.filter((event) => event.sourceType === "importedMatch" || event.importedMatchId).length,
    [approvedEvents]
  );

  const pendingEvents = useMemo(
    () => events.filter((event) => (event.approvalStatus || "approved") === "pending"),
    [events]
  );

  const boothRows = useMemo(
    () => countingRows.map((row, index) => {
      const totalScore = Number(row.aggregateScore ?? row.rawScore ?? 0);
      const rowCategories = normalizeCategoryList(row.categories);
      const teamName = String(row.state || "").trim();
      const identityMetaItems = [
        String(row.division || "").trim(),
        ...rowCategories.slice(0, 2),
      ].filter(Boolean);
      return {
        ...row,
        categories: rowCategories,
        identityMetaItems,
        sk: row.placementSortKey || row.entryId || row.competitorExternalId || `tournament-row-${index}`,
        totalScore: Number.isFinite(totalScore) ? totalScore : 0,
        scoreDisplay: scoreLabel(row),
        competitorName: row.competitorName || row.competitorExternalId || "Competitor",
        teamName,
        reviewStatus: "confirmed",
      };
    }),
    [countingRows]
  );

  const divisionFilterOptions = useMemo(
    () => uniqueSortedValues(boothRows, (row) => row.division),
    [boothRows]
  );
  const categoryFilterOptions = useMemo(
    () => uniqueSortedValues(boothRows, (row) => row.categories),
    [boothRows]
  );
  const filteredBoothRows = useMemo(
    () => boothRows.filter((row) => {
      const matchesDivision = !displayDivisionFilter || normalizeDisplayFilterValue(row.division) === displayDivisionFilter;
      const matchesCategory = !displayCategoryFilter || normalizeCategoryList(row.categories).includes(displayCategoryFilter);
      return matchesDivision && matchesCategory;
    }),
    [boothRows, displayCategoryFilter, displayDivisionFilter]
  );

  const boothLeaderScore = filteredBoothRows.length > 0 ? Number(filteredBoothRows[0].totalScore || 0) : null;
  const tournamentDefaultBannerSrc = defaultTournamentBannerSrc(tournament);
  const tournamentBannerLinks = usesV2Membership && tournament?.competitionFamily === "bullseye"
    ? null : TOURNAMENT_LEADERBOARD_BANNER_LINK_AREAS;
  const tournamentBannerSrc = useMemo(() => resolveTournamentBannerSrc(tournament), [tournament]);
  const boothMatch = useMemo(() => ({
    matchName: tournament?.name || "Tournament Leaderboard",
    location: `${formatDate(tournament?.startDate)} - ${formatDate(tournament?.endDate)}`,
  }), [tournament]);

  useEffect(() => {
    let active = true;
    const targetBannerSrc = tournamentBannerSrc || tournamentDefaultBannerSrc;

    if (targetBannerSrc === tournamentDefaultBannerSrc) {
      setReadyTournamentBannerSrc(tournamentDefaultBannerSrc);
      return () => {
        active = false;
      };
    }

    setReadyTournamentBannerSrc(tournamentDefaultBannerSrc);

    preloadLeaderboardBanner(targetBannerSrc)
      .then((loadedSrc) => {
        if (active) {
          setReadyTournamentBannerSrc(loadedSrc);
        }
      })
      .catch(() => {
        if (active) {
          setReadyTournamentBannerSrc(tournamentDefaultBannerSrc);
        }
      });

    return () => {
      active = false;
    };
  }, [tournamentBannerSrc, tournamentDefaultBannerSrc]);
  const detailSummaryItems = directorMode
    ? [
        ["Approved", readFailures.events ? "Unavailable" : eventCounts.approved],
        ["Pending", readFailures.events ? "Unavailable" : eventCounts.pending],
        ["Imported", readFailures.events ? "Unavailable" : approvedImportedCount],
        ["Leaderboard", standingsUnavailable ? "Unavailable" : countingRows.length],
      ]
    : [
        ["Included", readFailures.events ? "Unavailable" : approvedEvents.length],
        ["Competitors", standingsUnavailable ? "Unavailable" : countingRows.length],
        ["Attempts", attemptsUnavailable ? "Unavailable" : allAttemptRows.length],
        ["Imported", readFailures.events ? "Unavailable" : approvedImportedCount],
      ];
  const publicStandingRows = !directorMode && !showFullPublicStandings
    ? countingRows.slice(0, 10)
    : countingRows;
  const publicSponsorBanners = !directorMode && String(tournament?.tournamentId || "").toUpperCase() === NRA_ARC_250_TOURNAMENT_ID
    ? NRA_ARC_LEADERBOARD_BANNERS
    : [];
  const hasMorePublicStandings = !directorMode && countingRows.length > publicStandingRows.length;
  const publicTournamentHasNoResults = !directorMode
    && detailWarnings.length === 0
    && countingRows.length === 0
    && visibleIncludedEvents.length === 0;
  const tournamentReturnPath = tournamentId
    ? `${directorMode ? "/matches/manage/tournaments" : "/tournaments"}/${encodeURIComponent(tournamentId)}`
    : directorMode
      ? "/matches/manage/tournaments"
      : "/tournaments";
  const signInPath = buildAuthReturnPath(
    "/auth/sign-in",
    buildCurrentReturnTo(location, tournamentReturnPath)
  );
  const documentTitle = directorMode
    ? `${tournament?.name ? `${tournament.name} Workbench` : "Tournament Workbench"} | Range Pocket Competition`
    : `${tournament?.name || "Tournament"} | Range Pocket Competition`;
  const apiEnvironment = getCompetitionApiEnvironment();

  const workbenchTasks = [{ value: "matches", label: "Matches & review" }, ...((tournament?.competitionFamily === "arc" && tournament?.allowImportedMatches !== false) ? [{ value: "import", label: "Import scores" }] : []), { value: "display", label: "Display & standings" }, { value: "access", label: "Access & policy" }];
  const workbenchTask = workbenchTasks.some(task => task.value === searchParams.get("task")) ? searchParams.get("task") : "matches";
  const showTask = value => !directorMode || workbenchTask === value;
  function selectWorkbenchTask(value) {
    const next = new URLSearchParams(searchParams);
    value === "matches" ? next.delete("task") : next.set("task", value);
    setSearchParams(next, { replace: true, state: location.state });
  }
  const tournamentManagementSection = canManageTournament && !leaderboardOnly && !readFailures.events ? (
    <section className="leaderboard-card tournament-management-section director-tournament-workbench">
      <TournamentSectionHeader
        eyebrow={directorMode ? "Tournament Workbench" : "Owner Management"}
        title={directorMode ? workbenchTasks.find(task => task.value === workbenchTask).label : "Controls"}
      >
        {directorMode && (
          <Link to={`/tournaments/${encodeURIComponent(tournamentId)}`} className="registration-auth-secondary leaderboard-inline-link">
            Public View
          </Link>
        )}
      </TournamentSectionHeader>
      {directorMode && !usesV2Membership && (
        <p className="tournament-workbench-lede">
          Attach existing ARC matches, review incoming event requests, and keep the public display current from one workbench.
        </p>
      )}
      {directorMode && <div className="tournament-task-navigation">
        <nav aria-label="Tournament tasks">{workbenchTasks.map(task => <button type="button" key={task.value} aria-current={workbenchTask === task.value ? "page" : undefined} onClick={() => selectWorkbenchTask(task.value)}>{task.label}{task.value === "matches" && eventCounts.pending > 0 ? ` · ${eventCounts.pending} pending` : ""}</button>)}</nav>
        <div className="tournament-task-mobile"><DiscoverySelect label="Tournament task" value={workbenchTask} onChange={selectWorkbenchTask} options={workbenchTasks} /></div>
      </div>}

      {(lifecycleMessage || lifecycleError || adminMessage || adminError) && (
        <div className="tournament-message-stack">
          {lifecycleMessage && <div className="leaderboard-success-inline">{lifecycleMessage}</div>}
          {lifecycleError && <div className="leaderboard-error-inline">{lifecycleError}</div>}
          {adminMessage && <div className="leaderboard-success-inline">{adminMessage}</div>}
          {adminError && <div className="leaderboard-error-inline">{adminError}</div>}
        </div>
      )}

      {(bannerMessage || bannerError) && (
        <div className="tournament-message-stack">
          {bannerMessage && <div className="leaderboard-success-inline">{bannerMessage}</div>}
          {bannerError && <div className="leaderboard-error-inline">{bannerError}</div>}
        </div>
      )}

      <div className="tournament-workbench-primary-grid" hidden={directorMode && !["matches", "display"].includes(workbenchTask)}>
        {canAttachExistingArcMatch && (
          <form hidden={!showTask("matches")} onSubmit={handleAttachExistingArcMatch} className="tournament-management-panel tournament-management-panel--wide tournament-attach-panel">
            <div className="tournament-panel-title">Attach Existing ARC Match</div>
            <div className="registration-readiness-strip tournament-panel-readiness-strip">
              <div className={`registration-readiness-item ${attachableOwnedArcMatches.length > 0 ? "is-ready" : "is-neutral"}`}>
                <span>Available</span>
                <strong>{readFailures.ownedMatches ? "Unavailable" : attachableOwnedArcMatches.length}</strong>
              </div>
              <div className={`registration-readiness-item ${attachableArcEvents.length > 0 ? "is-ready" : "is-neutral"}`}>
                <span>Linked</span>
                <strong>{attachableArcEvents.length}</strong>
              </div>
            </div>
            <label className="leaderboard-field tournament-control-field">
              <span>Match</span>
              <select
                className="match-sort"
                value={attachMatchId}
                onChange={(event) => {
                  setAttachMatchId(event.target.value);
                  setAttachMessage("");
                  setAttachError("");
                }}
                disabled={attachBusy || attachableOwnedArcMatches.length === 0}
              >
                <option value="">{readFailures.ownedMatches ? "Matches unavailable" : "Choose one of your ARC matches"}</option>
                {attachableOwnedArcMatches.map((match) => (
                  <option key={match.matchId} value={match.matchId}>
                    {match.matchName} · {formatDate(match.startDate || match.matchDate)}
                  </option>
                ))}
              </select>
            </label>
            {!readFailures.ownedMatches && attachableOwnedArcMatches.length === 0 && (
              <div className="tournament-inline-empty">
                <span>No unlinked owned ARC matches are available</span>
              </div>
            )}
            {selectedAttachMatch && (
              <div className="tournament-attach-match-summary">
                <span>{matchStatusLabel(selectedAttachMatch.status || "scheduled")}</span>
                <span>{formatDate(selectedAttachMatch.startDate || selectedAttachMatch.matchDate)}</span>
                <span>{selectedAttachMatch.rangeName || selectedAttachMatch.state || "Location not set"}</span>
              </div>
            )}
            <div className="tournament-panel-actions">
              <button
                type="submit"
                className="match-search-button"
                disabled={attachBusy || !selectedAttachMatch}
              >
                {attachBusy ? "Attaching..." : "Attach Match"}
              </button>
            </div>
            {attachMessage && <div className="leaderboard-success-inline">{attachMessage}</div>}
            {attachError && <div className="leaderboard-error-inline">{attachError}</div>}
          </form>
        )}

        <div hidden={!showTask("matches")} className="tournament-management-panel tournament-workbench-ops-panel">
          <div>
            <div className="tournament-panel-title">Operations</div>
            <div className="registration-readiness-strip tournament-panel-readiness-strip tournament-ops-readiness-strip">
              <div className={`registration-readiness-item ${isTournamentActive(tournament) ? "is-ready" : "is-neutral"}`}>
                <span>Status</span>
                <strong>{statusLabel(tournament?.status || "draft")}</strong>
              </div>
              <div className={`registration-readiness-item ${eventCounts.pending > 0 ? "is-missing" : "is-ready"}`}>
                <span>Review</span>
                <strong>{eventCounts.pending}</strong>
              </div>
            </div>
          </div>
          <div className="tournament-ops-controls">
            <label className="leaderboard-field tournament-control-field">
              <span>Status</span>
              <select
                className="match-sort"
                value={tournament?.status || "draft"}
                onChange={(event) => handleTournamentStatus(event.target.value)}
                disabled={Boolean(statusBusy)}
              >
                {Object.entries(TOURNAMENT_STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
            <div className="tournament-cache-control">
              <span>{tournamentPublishedAt(tournament) ? `Standings published ${formatDate(tournamentPublishedAt(tournament))}` : "Standings refresh pending"}</span>
              <button
                type="button"
                className="registration-auth-secondary leaderboard-inline-link"
                onClick={handleRecalculateLeaderboard}
                disabled={recalculating || !isTournamentActive(tournament)}
              >
                {recalculating ? "Recalculating..." : "Recalculate"}
              </button>
            </div>
          </div>
          {statusBusy && <div className="leaderboard-meta">Updating {statusLabel(statusBusy)}...</div>}
        </div>

        <div hidden={!showTask("display")} className="tournament-management-panel tournament-banner-panel">
          <div>
            <div className="tournament-panel-title">Display Banner</div>
            {tournament?.leaderboardBannerUrl ? (
              <img
                className="director-registration-banner-thumb tournament-banner-thumb"
                src={readyTournamentBannerSrc}
                alt="Current tournament leaderboard banner"
              />
            ) : (
              <img
                className="director-registration-banner-thumb tournament-banner-thumb"
                src={tournamentDefaultBannerSrc}
                alt="Default tournament leaderboard banner"
              />
            )}
          </div>
          <div className="tournament-ops-controls">
            <label className={`registration-auth-secondary director-file-button ${bannerBusy ? "disabled" : ""}`}>
              <input
                className="owner-banner-file-input"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleTournamentBannerUpload}
                disabled={bannerBusy}
              />
              {bannerBusy ? "Uploading..." : "Upload Banner"}
            </label>
            <div className="leaderboard-meta">PNG, JPG, or WebP under 5 MB.</div>
          </div>
        </div>
      </div>

      {usesV2Membership && showTask("matches") && <TournamentMatchManager key={tournamentId} tournament={tournament} events={events} userId={currentUserId} onCommitted={refreshCommittedMembership} />}

      <div className="tournament-management-grid tournament-workbench-secondary-grid">
        <div hidden={!showTask("matches") || usesV2Membership} className="tournament-management-panel tournament-management-panel--wide">
          <div className="tournament-panel-title">Event Review</div>
          {pendingEvents.length === 0 ? (
            <div className="tournament-empty-panel list-empty-panel tournament-workbench-empty">
              <div className="owner-registration-section-label">Review</div>
              <strong>No event requests</strong>
              <div className="empty-filter-summary" aria-label="Event review state">
                <span>Pending: 0</span>
              </div>
            </div>
          ) : (
            <TournamentEventCards
              events={pendingEvents}
              tournament={tournament}
              canManageTournament={canManageTournament}
              eventBusyKey={eventBusyKey}
              onEventStatus={handleEventStatus}
              resultCountByEvent={resultCountByEvent}
              resultsUnavailable={Boolean(readFailures.results)}
              displayActionLabel="Open standings"
            />
          )}
        </div>

        {tournament?.competitionFamily === "arc" && tournament.allowImportedMatches !== false && (
          <form hidden={!showTask("import")} onSubmit={handleImportArcJson} className="tournament-management-panel tournament-management-panel--wide">
            <div className="tournament-panel-title">Import ARC JSON</div><p className="tournament-context-copy">Add scores from an ARC export file. Create a separate imported match, or attach the file to a match already linked from Range Pocket.</p>
            <div className="registration-readiness-strip tournament-panel-readiness-strip">
              <div className={`registration-readiness-item ${isTournamentActive(tournament) ? "is-ready" : "is-missing"}`}>
                <span>Tournament</span>
                <strong>{isTournamentActive(tournament) ? "Active" : "Locked"}</strong>
              </div>
              <div className={`registration-readiness-item ${attachableArcEvents.length > 0 ? "is-ready" : "is-neutral"}`}>
                <span>Linked ARC matches</span>
                <strong>{attachableArcEvents.length}</strong>
              </div>
            </div>
            <label className="leaderboard-field tournament-control-field">
              <span>Import Mode</span>
              <select
                className="match-sort"
                value={importMode}
                onChange={(event) => setImportMode(event.target.value)}
                disabled={importBusy}
              >
                <option value="new">Create imported match</option>
                <option value="existing" disabled={attachableArcEvents.length === 0}>Attach to existing match</option>
              </select>
            </label>
            {importMode === "existing" && (
              <label className="leaderboard-field">
                <span>Match</span>
                <select
                  value={importEventLinkId}
                  onChange={(event) => setImportEventLinkId(event.target.value)}
                  disabled={importBusy || attachableArcEvents.length === 0}
                >
                  <option value="">Choose an ARC tournament match</option>
                  {attachableArcEvents.map((event) => (
                    <option key={event.eventLinkId} value={event.eventLinkId}>
                      {event.displayName || event.nativeMatchId}
                    </option>
                  ))}
                </select>
                {attachableArcEvents.length === 0 && (
                  <div className="tournament-inline-empty">
                    <span>No native ARC matches linked</span>
                  </div>
                )}
              </label>
            )}
            <label className="leaderboard-field">
              <span>Range Name</span>
              <input
                className="match-search"
                value={importLocation.rangeName}
                onChange={(event) => setImportLocationField("rangeName", event.target.value)}
                disabled={importBusy}
              />
            </label>
            <label className="leaderboard-field">
              <span>Address</span>
              <input
                className="match-search"
                value={importLocation.rangeAddress}
                onChange={(event) => setImportLocationField("rangeAddress", event.target.value)}
                disabled={importBusy}
              />
            </label>
            <label className="leaderboard-field">
              <span>State</span>
              <input
                className="match-search"
                value={importLocation.state}
                onChange={(event) => setImportLocationField("state", event.target.value)}
                disabled={importBusy}
              />
            </label>
            <label className="leaderboard-field">
              <span>Zip Code</span>
              <input
                className="match-search"
                value={importLocation.zipCode}
                onChange={(event) => setImportLocationField("zipCode", event.target.value)}
                disabled={importBusy}
              />
            </label>
            <label className="tournament-file-picker">
              <span>{importFile ? importFile.name : "Choose JSON File"}</span>
              <input
                type="file"
                accept=".json,application/json"
                onChange={(event) => {
                  setImportFile(event.target.files?.[0] || null);
                  setImportMessage("");
                  setImportError("");
                }}
                disabled={importBusy}
              />
            </label>
            <div className="tournament-panel-actions">
              <button
                type="submit"
                className="match-search-button"
                disabled={importBusy || !isTournamentActive(tournament)}
              >
                {importBusy ? "Importing..." : "Import JSON"}
              </button>
            </div>
            {importMessage && <div className="leaderboard-success-inline">{importMessage}</div>}
            {importError && <div className="leaderboard-error-inline">{importError}</div>}
          </form>
        )}

        <div hidden={!showTask("access")} className="tournament-management-panel tournament-management-panel--wide">
          <div className="tournament-panel-title">Organizer Access</div>
          {readFailures.participants ? (
            <div className="tournament-inline-empty"><span>Organizer requests unavailable</span></div>
          ) : participants.length === 0 ? (
            <div className="tournament-inline-empty">
              <span>No organizer access requests</span>
            </div>
          ) : (
            <div className="tournament-participant-list">
              {participants.map((participant) => (
                <article key={participant.participantUserId} className="tournament-participant-card">
                  <div>
                    <strong>{participant.participantDisplayName || participant.contactEmail || "Organizer"}</strong>
                    <div className="leaderboard-meta">{participant.contactEmail || "User ID on file"}</div>
                    {!participant.contactEmail && (
                      <TechnicalIdDisclosure label="Organizer ID" value={participant.participantUserId} />
                    )}
                  </div>
                  <span className="open-match-chip">{participantStatusLabel(participant.participantStatus)}</span>
                  <div className="tournament-event-card-actions">
                    {participant.participantStatus !== "approved" && (
                      <button
                        type="button"
                        className="match-search-button"
                        onClick={() => handleParticipantStatus(participant.participantUserId, "approved")}
                        disabled={participantBusy || !isTournamentActive(tournament)}
                      >
                        Approve
                      </button>
                    )}
                    {participant.participantStatus === "approved" ? (
                      <button
                        type="button"
                        className="match-search-button"
                        onClick={() => handleParticipantStatus(participant.participantUserId, "revoked")}
                        disabled={participantBusy || !isTournamentActive(tournament)}
                      >
                        Revoke
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="match-search-button"
                        onClick={() => handleParticipantStatus(participant.participantUserId, "rejected")}
                        disabled={participantBusy || !isTournamentActive(tournament)}
                      >
                        Reject
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
          {participantMessage && <div className="leaderboard-success-inline">{participantMessage}</div>}
          {participantError && <div className="leaderboard-error-inline">{participantError}</div>}
        </div>
      </div>
    </section>
  ) : null;

  function handleDisplayTopCountChange(nextValue) {
    const normalizedValue = normalizeDisplayRowCount(nextValue, 20);
    setDisplayTopCount(normalizedValue);
    const nextParams = new URLSearchParams(searchParams);
    if (normalizedValue === 20) {
      nextParams.delete("rows");
    } else {
      nextParams.set("rows", String(normalizedValue));
    }
    nextParams.delete("top");
    setSearchParams(nextParams, { replace: true, state: location.state });
  }

  function handleDisplayFilterChange(filterName, nextValue) {
    const normalizedValue = normalizeDisplayFilterValue(nextValue);
    if (filterName === "division") {
      setDisplayDivisionFilter(normalizedValue);
    }
    if (filterName === "category") {
      setDisplayCategoryFilter(normalizedValue);
    }

    const nextParams = new URLSearchParams(searchParams);
    if (normalizedValue) {
      nextParams.set(filterName, normalizedValue);
    } else {
      nextParams.delete(filterName);
    }
    setSearchParams(nextParams, { replace: true, state: location.state });
  }

  if (leaderboardOnly && !loading && !error && usesV2Membership) {
    return <section className="leaderboard-page leaderboard-page-booth"><Helmet><title>{`${tournament.name} Leaderboard`} | Range Pocket Competition</title></Helmet><TournamentStandingsV2 key={tournamentId} tournament={tournament} display bannerSrc={readyTournamentBannerSrc} bannerLinkAreas={tournamentBannerLinks} onExit={() => navigate(displayReturn(location, `/tournaments/${encodeURIComponent(tournamentId)}`))} /></section>;
  }

  if (leaderboardOnly) {
    return (
      <section className="leaderboard-page leaderboard-page-booth">
        <Helmet>
          <title>{`${tournament?.name || "Tournament"} Leaderboard`} | Range Pocket Competition</title>
        </Helmet>

        {loading && (
          <BoothDisplayStatus
            title="Loading leaderboard"
            message="Preparing the tournament display"
            bannerSrc={TOURNAMENT_DEFAULT_LEADERBOARD_BANNER_SRC}
            bannerLinkAreas={TOURNAMENT_LEADERBOARD_BANNER_LINK_AREAS}
          />
        )}
        {!loading && (error || standingsUnavailable) && (
          <BoothDisplayStatus
            title="Leaderboard unavailable"
            message={error || detailWarnings.join(" ")}
            bannerSrc={TOURNAMENT_DEFAULT_LEADERBOARD_BANNER_SRC}
            bannerLinkAreas={TOURNAMENT_LEADERBOARD_BANNER_LINK_AREAS}
            actions={(
              <>
              <button type="button" className="match-search-button" disabled={retryingReads} onClick={error ? () => window.location.reload() : retryTournamentReads}>
                {retryingReads ? "Retrying..." : "Try Again"}
              </button>
              <Link to={`/tournaments/${encodeURIComponent(tournamentId || "")}`} className="registration-auth-secondary leaderboard-inline-link">
                Back to Tournament
              </Link>
              </>
            )}
          />
        )}
        {!loading && !error && !standingsUnavailable && tournament && (
          <BoothLeaderboardDisplay
            match={boothMatch}
            bannerSrc={readyTournamentBannerSrc}
            bannerLinkAreas={TOURNAMENT_LEADERBOARD_BANNER_LINK_AREAS}
            rows={filteredBoothRows}
            topCount={displayTopCount}
            onTopCountChange={handleDisplayTopCountChange}
            onExit={() => navigate(displayReturn(location, `/tournaments/${encodeURIComponent(tournament.tournamentId)}`))}
            leaderScore={boothLeaderScore}
            rowClassName="tournament-booth-row"
            teamColumnLabel="State"
            scoreColumnLabel="Score"
            showPerformanceColumn={false}
            emptyMessage="No approved tournament results yet"
            sponsorBanners={publicSponsorBanners}
            filterControls={(
              <>
                {divisionFilterOptions.length > 0 && (
                  <label className="booth-top-control booth-filter-control">
                    <span>Division</span>
                    <select
                      value={displayDivisionFilter}
                      onChange={(event) => handleDisplayFilterChange("division", event.target.value)}
                    >
                      <option value="">All</option>
                      {divisionFilterOptions.map((division) => (
                        <option key={division} value={division}>{division}</option>
                      ))}
                    </select>
                  </label>
                )}
                {categoryFilterOptions.length > 0 && (
                  <label className="booth-top-control booth-filter-control">
                    <span>Category</span>
                    <select
                      value={displayCategoryFilter}
                      onChange={(event) => handleDisplayFilterChange("category", event.target.value)}
                    >
                      <option value="">All</option>
                      {categoryFilterOptions.map((category) => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </label>
                )}
              </>
            )}
          />
        )}
      </section>
    );
  }

  if (!loading && !error && usesV2Membership && !directorMode) {
    return <><Helmet><title>{documentTitle}</title></Helmet><TournamentStandingsV2 key={tournamentId} tournament={tournament} events={events} standalone eventsUnavailable={Boolean(readFailures.events)} onRetryEvents={retryTournamentReads} /></>;
  }

  return (
    <section className={`leaderboard-page tournament-page portal-surface ${directorMode ? "tournament-director-page" : ""}`}>
      <Helmet>
        <title>{documentTitle}</title>
        {error && !directorMode && <meta name="robots" content="noindex,follow" />}
      </Helmet>

      {loading && (
        <div className="leaderboard-card list-empty-panel status-loading-panel status-loading-panel--detail">
          <div className="owner-registration-section-label">Tournament detail</div>
          <strong>Loading standings and included matches</strong>
          <span>Checking public result approval status</span>
          <div className="status-loading-skeleton" aria-hidden="true">
            <i />
            <i />
            <i />
          </div>
        </div>
      )}
      {error && !directorMode && (
        <CompetitionErrorState
          eyebrow="Tournament"
          title="Tournament unavailable"
          message={error}
          links={[
            { to: "/tournaments", label: "Tournaments" },
            currentUserId ? { to: "/competition/account", label: "Account" } : { to: signInPath, label: "Sign In" },
          ]}
          className="tournament-unavailable-panel"
        />
      )}
      {error && directorMode && !(directorMode && !tournament && !currentUserId) && <div className="leaderboard-error">{error}</div>}

      {directorMode && !loading && !tournament && !currentUserId && (
        <CompetitionAuthGate
          eyebrow="Tournament admin"
          title="Sign in to manage this tournament"
          signInPath={signInPath}
          links={[{ to: "/tournaments", label: "Tournaments" }]}
        >
          This tournament may be private, archived, or owner-only. Sign in with an owner or approved organizer account to open the workbench.
        </CompetitionAuthGate>
      )}

      {directorMode && !loading && tournament && !currentUserId && (
        <CompetitionAuthGate
          eyebrow="Tournament admin"
          title="Sign in to manage this tournament"
          signInPath={signInPath}
          links={[
            { to: `/tournaments/${encodeURIComponent(tournament.tournamentId)}`, label: "Public Tournament" },
            { to: "/tournaments", label: "Tournaments" },
          ]}
        >
          Tournament admin is for owners and approved organizers. Public standings and included matches are available without entering the director workspace.
        </CompetitionAuthGate>
      )}

      {!loading && tournament && (!directorMode || currentUserId) && (
        <>
          <div className={`leaderboard-detail-hero ${directorMode ? "tournament-director-hero" : ""}`}>
            <div className="leaderboard-detail-copy">
              <Link to={directorMode ? "/matches/manage/tournaments" : "/tournaments"} className="leaderboard-detail-back-link">
                <span aria-hidden="true">←</span>
                <span>{directorMode ? "Tournament Admin" : "Tournaments"}</span>
              </Link>
              <h1>{tournament.name || "Untitled Tournament"}</h1>
              <div className="leaderboard-detail-chip-row">
                <span className={`status-pill status-${String(tournament.status || "draft").replace(/\s+/g, "_")}`}>
                  {statusLabel(tournament.status || "draft")}
                </span>
                <span className="open-match-chip">{familyLabel(tournament.competitionFamily)}</span>
                <span className="open-match-chip">{aggregationLabel(tournament.aggregationMode)}</span>
                <span className="open-match-chip">{sourcePolicyLabel(tournament)}</span>
                {directorMode && apiEnvironment.shouldShowIndicator && (
                  <span className="open-match-chip">{apiEnvironment.label}</span>
                )}
              </div>
              {directorMode && <p className="tournament-context-copy">{formatDate(tournament.startDate)} – {formatDate(tournament.endDate)}</p>}
              <div className="leaderboard-detail-actions">
                {directorMode && canManageTournament && isTournamentV2Enabled() && ["arc", "bullseye"].includes(tournament.competitionFamily) && <Link to={`/matches/manage/tournaments/${encodeURIComponent(tournamentId)}/settings`} className="registration-auth-secondary leaderboard-inline-link">Tournament settings</Link>}
                {publicTournamentHasNoResults && !directorMode ? (
                  <>
                    <Link to="/matches" className="match-search-button leaderboard-inline-link">
                      Browse Matches
                    </Link>
                    <span className="registration-auth-secondary leaderboard-inline-link tournament-pending-action" aria-disabled="true">
                      Standings pending
                    </span>
                  </>
                ) : (
                  <>
                    <Link to={tournamentLeaderboardPath(tournament.tournamentId)} className="match-search-button leaderboard-inline-link">
                      Open standings
                    </Link>
                    <Link to={tournamentLeaderboardAllRowsPath(tournament.tournamentId)} className="registration-auth-secondary leaderboard-inline-link">
                      All scores
                    </Link>
                  </>
                )}
              </div>
            </div>
            <div className="leaderboard-detail-map-card tournament-detail-summary-card">
              {!directorMode && <div>
                <span className="leaderboard-detail-kicker">Tournament Window</span>
                <strong>{formatDate(tournament.startDate)} - {formatDate(tournament.endDate)}</strong>
              </div>}
              <div className="registration-readiness-strip tournament-detail-readiness-strip">
                {detailSummaryItems.map(([label, value]) => (
                  <div key={label} className={`registration-readiness-item ${Number(value) > 0 ? "is-ready" : "is-neutral"}`}>
                    <span>{label}</span>
                    <strong>{value}</strong>
                  </div>
                ))}
              </div>
              <div className="leaderboard-detail-map-meta">
                <span>Updated</span>
                <strong>{tournamentPublishedAt(tournament) ? formatDateTime(tournamentPublishedAt(tournament)) : "Pending refresh"}</strong>
              </div>
            </div>
          </div>

          {directorMode && tournamentManagementSection}
          {directorMode && canManageTournament && readFailures.events && attachMessage && (
            <div role="status" className="leaderboard-success-inline">{attachMessage}</div>
          )}
          {detailWarnings.length > 0 && (
            <div className="leaderboard-card leaderboard-warning">
              Showing available tournament data. {detailWarnings.join(" ")}
              <div className="tournament-section-actions">
                <button type="button" className="match-search-button tournament-read-retry" disabled={retryingReads} onClick={retryTournamentReads}>
                  {retryingReads ? "Retrying..." : "Retry unavailable data"}
                </button>
              </div>
            </div>
          )}

          {!directorMode && (
            <section className="leaderboard-card tournament-display-section tournament-included-preview-section">
              <TournamentSectionHeader eyebrow="Matches" title="Included Matches">
                <span className="open-match-chip">{readFailures.events ? "Unavailable" : `${visibleIncludedEvents.length} approved`}</span>
              </TournamentSectionHeader>
              <p className="tournament-context-copy">
                This series uses {aggregationLabel(tournament.aggregationMode).toLowerCase()} scoring. Approved included matches feed the public standings after results are posted.
              </p>
              <TournamentEventCards
                events={visibleIncludedEvents}
                tournament={tournament}
                canManageTournament={false}
                eventBusyKey={eventBusyKey}
                onEventStatus={handleEventStatus}
                resultCountByEvent={resultCountByEvent}
                unavailable={Boolean(readFailures.events)}
                resultsUnavailable={Boolean(readFailures.results)}
                displayActionLabel="Open standings"
                emptyText={readFailures.events ? "Included matches unavailable" : "No approved matches are included yet"}
              />
            </section>
          )}

          {usesV2Membership && canManageTournament ? (showTask("display") && <TournamentStandingsV2 key={tournamentId} tournament={tournament} events={events} director eventsUnavailable={Boolean(readFailures.events)} onRetryEvents={retryTournamentReads} />) : publicTournamentHasNoResults ? (
            <section className="leaderboard-card tournament-public-empty-summary list-empty-panel">
              <div>
                <div className="owner-registration-section-label">Public standings</div>
                <h2>Standings will appear after included match results are approved</h2>
                <p className="empty-state-copy">
                  This tournament is published, but it does not have approved public match results yet. Check back after match staff approve scores, or browse open matches.
                </p>
              </div>
              <div className="empty-filter-summary" aria-label="Tournament public status">
                <span>{aggregationLabel(tournament.aggregationMode)}</span>
                <span>Approved matches: 0</span>
                <span>Standings rows: 0</span>
              </div>
              <div className="tournament-section-actions">
                <Link to="/matches" className="match-search-button leaderboard-inline-link">
                  Browse Matches
                </Link>
                <Link to="/tournaments" className="registration-auth-secondary leaderboard-inline-link">
                  All Tournaments
                </Link>
              </div>
            </section>
          ) : (
            <div className="leaderboard-stack" hidden={!showTask("display")}>
              <section id="tournament-results" className="leaderboard-card leaderboard-results-action-bar tournament-results-section">
                <div className="leaderboard-card-row leaderboard-card-row--wrap">
                  <h2>Standings</h2>
                  <span className="open-match-chip">{standingsUnavailable ? "Unavailable" : `${countingRows.length} standings rows`}</span>
                </div>
              </section>

              {directorMode ? (
                <details className="leaderboard-card tournament-workbench-standings">
                  <summary>
                    <span>
                      <strong>Standings preview</strong>
                      <small>Open when you need to review public ranking output.</small>
                    </span>
                    <span className="open-match-chip">{standingsUnavailable ? "Unavailable" : `${publicStandingRows.length} shown`}</span>
                  </summary>
                  <TournamentLeaderboardRanking rows={publicStandingRows} tournament={tournament} unavailable={standingsUnavailable} />
                  {hasMorePublicStandings && (
                    <div className="tournament-section-actions tournament-attempt-toggle-row">
                      <button
                        type="button"
                        className="registration-auth-secondary"
                        onClick={() => setShowFullPublicStandings(true)}
                      >
                        Show All
                      </button>
                    </div>
                  )}
                  {allAttemptRows.length > 0 && (
                    <div className="tournament-section-actions tournament-attempt-toggle-row">
                      <button
                        type="button"
                        className="match-search-button"
                        onClick={() => setShowAllAttempts((current) => !current)}
                      >
                        {showAllAttempts ? "Hide Attempts" : "Show Attempts"}
                      </button>
                    </div>
                  )}
                  {showAllAttempts && (
                    <AttemptsTable rows={allAttemptRows} eventNameById={eventNameById} />
                  )}
                </details>
              ) : (
                <section className="leaderboard-card">
                  <TournamentLeaderboardRanking rows={publicStandingRows} tournament={tournament} sponsorBanners={publicSponsorBanners} unavailable={standingsUnavailable} />
                  {hasMorePublicStandings && (
                    <div className="tournament-section-actions tournament-attempt-toggle-row">
                      <button
                        type="button"
                        className="registration-auth-secondary"
                        onClick={() => setShowFullPublicStandings(true)}
                      >
                        Show All
                      </button>
                    </div>
                  )}
                  {allAttemptRows.length > 0 && (
                    <div className="tournament-section-actions tournament-attempt-toggle-row">
                      <button
                        type="button"
                        className="match-search-button"
                        onClick={() => setShowAllAttempts((current) => !current)}
                      >
                        {showAllAttempts ? "Hide Attempts" : "Show Attempts"}
                      </button>
                    </div>
                  )}
                  {showAllAttempts && (
                    <AttemptsTable rows={allAttemptRows} eventNameById={eventNameById} />
                  )}
                </section>
              )}
            </div>
          )}

          {directorMode && !publicTournamentHasNoResults && !(usesV2Membership && canManageTournament) && <section hidden={!showTask("matches")} className="leaderboard-card tournament-display-section">
            <TournamentSectionHeader eyebrow="Matches" title={canManageTournament ? "Linked Matches" : "Included Matches"}>
              {directorMode && canManageTournament && canCreateArcMatchForTournament && (
                <Link
                  to={`/matches/manage/new?competition=nra_arc&tournamentId=${encodeURIComponent(tournament.tournamentId)}`}
                  className="match-search-button leaderboard-inline-link"
                >
                  Create ARC Match
                </Link>
              )}
            </TournamentSectionHeader>
            <TournamentEventCards
              events={visibleIncludedEvents}
              tournament={tournament}
              canManageTournament={directorMode && canManageTournament}
              eventBusyKey={eventBusyKey}
              onEventStatus={handleEventStatus}
              resultCountByEvent={resultCountByEvent}
              unavailable={Boolean(readFailures.events)}
              resultsUnavailable={Boolean(readFailures.results)}
              displayActionLabel="Open standings"
              emptyText={readFailures.events ? "Included matches unavailable" : canManageTournament ? "No matches are linked yet" : "No approved matches are included yet"}
            />
          </section>}

          {directorMode && !canManageTournament && (
            <section className={`leaderboard-card leaderboard-inline-action-panel tournament-organizer-access-panel ${directorMode ? "" : "tournament-organizer-access-panel--public"}`}>
              <div className="leaderboard-card-row leaderboard-card-row--wrap">
                <div>
                  <div className="owner-registration-section-label">For match directors</div>
                  <h2>Submit a match to this tournament</h2>
                  <div className="registration-readiness-strip tournament-organizer-readiness-strip">
                    <div className={`registration-readiness-item ${isTournamentActive(tournament) ? "is-ready" : "is-missing"}`}>
                      <span>Tournament</span>
                      <strong>{isTournamentActive(tournament) ? "Active" : "Closed"}</strong>
                    </div>
                    <div className={`registration-readiness-item ${hasApprovedOrganizerAccess ? "is-ready" : myParticipant?.participantStatus === "pending" ? "is-missing" : "is-neutral"}`}>
                      <span>Access</span>
                      <strong>{readFailures.myParticipation ? "Unavailable" : myParticipant ? participantStatusLabel(myParticipant.participantStatus) : "Not requested"}</strong>
                    </div>
                  </div>
                </div>
                {!currentUserId && (
                  <Link to={signInPath} className="registration-auth-secondary leaderboard-inline-link">
                    Sign In to Request Access
                  </Link>
                )}
                {currentUserId && !readFailures.myParticipation && isTournamentActive(tournament) && (!myParticipant || myParticipant.participantStatus === "rejected" || myParticipant.participantStatus === "revoked") && (
                  <button
                    type="button"
                    className="registration-auth-secondary"
                    onClick={handleRequestTournamentAccess}
                    disabled={participantBusy}
                  >
                    {participantBusy ? "Sending..." : "Request Organizer Access"}
                  </button>
                )}
                {myParticipant?.participantStatus === "pending" && (
                  <span className="status-pill status-pending">Organizer Request Pending</span>
                )}
                {hasApprovedOrganizerAccess && (
                  <span className="status-pill status-approved">Organizer Access Approved</span>
                )}
                {canCreateArcMatchForTournament && (
                  <Link
                    to={`/matches/manage/new?competition=nra_arc&tournamentId=${encodeURIComponent(tournament.tournamentId)}`}
                    className="match-search-button leaderboard-inline-link"
                  >
                    Create ARC Match
                  </Link>
                )}
              </div>
              {participantMessage && <div className="leaderboard-success-inline">{participantMessage}</div>}
              {participantError && <div className="leaderboard-error-inline">{participantError}</div>}
            </section>
          )}

          {!usesV2Membership && matchLeaderboardGroups.length > 0 ? (
            <details hidden={!showTask("display")} className="leaderboard-card tournament-public-disclosure">
              <summary>
                <span>
                  <span className="owner-registration-section-label">Match Results</span>
                  <strong>Match leaderboards</strong>
                </span>
                <span className="open-match-chip">{matchLeaderboardGroups.length}</span>
              </summary>
              <MatchLeaderboardsSection groups={matchLeaderboardGroups} />
            </details>
          ) : null}


          {!usesV2Membership && competitorHistoryRows.length > 0 && (
            <details hidden={!showTask("display")} className="leaderboard-card tournament-public-disclosure">
              <summary>
                <span>
                  <span className="owner-registration-section-label">Results</span>
                  <strong>Competitor history</strong>
                </span>
                <span className="open-match-chip">{competitorHistoryRows.length}</span>
              </summary>
              <div className="leaderboard-table-wrap">
                <table className="leaderboard-table">
                  <thead>
                    <tr>
                      <th>Competitor</th>
                      <th>Counting Score</th>
                      <th>Attempts</th>
                      <th>Events</th>
                      <th>Latest</th>
                    </tr>
                  </thead>
                  <tbody>
                    {competitorHistoryRows.map((row) => (
                      <tr key={row.key}>
                        <td data-label="Competitor">
                          <div>{row.competitorName}</div>
                          {row.competitorIdentityText && <small>{row.competitorIdentityText}</small>}
                        </td>
                        <td data-label="Counting Score">{row.countingScore}</td>
                        <td data-label="Attempts">{row.countingCount}/{row.attemptCount}</td>
                        <td data-label="Events">{row.eventNames.join(", ") || "-"}</td>
                        <td data-label="Latest">{formatDate(row.latestDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {directorMode && (
            <section hidden={!showTask("access")} className="leaderboard-card tournament-info-section">
              <TournamentSectionHeader eyebrow="Information" title="Policy Summary" />
              <div className="leaderboard-detail-facts">
                {policyFacts.map(([label, value]) => (
                  <div key={label}>
                    <span>{label}</span>
                    <strong>{value}</strong>
                  </div>
                ))}
              </div>
            </section>
          )}

        </>
      )}
    </section>
  );
}

export const Tournaments_Page = {
  path: "/tournaments",
  element: <TournamentsPage />,
};

export const TournamentDetail_Page = {
  path: "/tournaments/:tournamentId",
  element: <TournamentDetailPage />,
};

export const DirectorTournamentDetail_Page = {
  path: "/matches/manage/tournaments/:tournamentId",
  element: <TournamentDetailPage directorMode />,
};

export const TournamentLeaderboard_Page = {
  path: "/tournaments/:tournamentId/leaderboard",
  element: <TournamentDetailPage leaderboardOnly />,
};
